import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, MapPin, CalendarDays, Clock, Calculator, ChevronDown, ChevronUp,
  Pause, Maximize2, Minimize2, Lock, Search, User, Plus, Minus, MoreVertical,
  Trash2, X, Percent, Banknote, CreditCard, AlertTriangle,
  CheckCircle, ShoppingBag, Package, Zap, Receipt, Building2, GitMerge, Package2, ImagePlus, Bell,
} from 'lucide-react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useCurrency } from '../../hooks/useCurrency'
import { usePermissions } from '../../hooks/usePermissions'
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import { getCustomerBalance, getAvailableCustomerAdvance } from '../../lib/contactBalances'
import { readReceiptExtras } from '../../components/documents/ReceiptExtras'
import { InvoiceReceipt } from '../../components/documents/InvoiceReceipt'
import { getReceivingAccounts, detectAccountType, normalisePaymentMethod, type SalesOrderPaymentMethod } from '../../lib/getPaymentAccounts'
import { resolveHeaderDiscountAmount, allocateHeaderDiscount, allocateProportional } from '../../lib/saleDiscount'
import { optimizeAndUploadProductImage } from '../../lib/productImages'
import { cn } from '../../utils/cn'
import POSCalculator from '../../components/pos/POSCalculator'
import {
  useActiveBundlesForLocation, bundleDerivedPrice, bundleAvailableQty,
  type ActiveBundle, type BundleComponent,
} from '../../lib/bundles'
import { useBusinessTaxSettings } from '../../hooks/useBusinessTaxSettings'
import { useCurrentUserPagePermissions } from '../../lib/permissions/usePagePermissions'
import { useOnlineOrderKpis } from '../../hooks/useOnlineOrderKpis'
import { unattendedOnlineOrderCount } from '../../lib/supabaseQueries'
import type { PageMeta } from '../../lib/permissions/pageMeta'

export const pageMeta: PageMeta = {
  key: 'sell.pos',
  label: 'Point of Sale',
  module: 'Trading',
  section: 'Sell',
  description: 'Point of sale interface',
  defaultPermissions: {
    owner: 'edit', admin: 'edit', manager: 'edit',
    accountant: 'none', cashier: 'edit', viewer: 'none',
  },
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

// ─── Types ────────────────────────────────────────────────────────────────────
type ProductRow = {
  id: string
  name: string
  sku: string | null
  barcode: string | null
  selling_price: number
  cost_price: number
  reorder_level: number
  image_url: string | null
  quantity: number | null
  product_categories: { id: string; name: string; color: string } | null
  vat_categories: { is_vatable: boolean } | null
}

type CartItem = {
  id: string          // product id (or product__variant), or "bundle:<id>" for bundles
  productId: string
  variantId: string | null
  name: string
  sku: string | null
  price: number
  // Catalogue floor (upward-only price): the item's catalogue price = the
  // product's (or variant's) selling_price. `price` may be raised ABOVE this but
  // never set below it — reductions go through a discount. Bundles are exempt.
  cataloguePrice: number
  qty: number
  discountType: 'fixed' | 'percent'
  discountValue: number
  isVatable: boolean
  total: number
  imageUrl: string | null
  // Bundle support: when bundleId is set this cart item is a bundle, price is
  // the bundle's derived selling price (sum of components.unit_qty * unit_price)
  // and components is a snapshot frozen at add time. Submitted to
  // post_sale_atomic as N expanded component rows, each with bundle_id +
  // bundle_name tags.
  bundleId?: string | null
  bundleName?: string | null
  components?: BundleComponent[]
}

type CustomerRow = {
  id: string
  name: string
  business_name?: string | null
  phone: string | null
  outstanding_balance: number
  credit_limit?: number
}

type CategoryRow = { id: string; name: string; color: string }

type PaymentAccount = {
  id: string
  name: string
  type: string
  account_type?: string
  current_balance?: number
  // For COA-based accounts: id === coa_account_id. For legacy: separate reference.
  coa_account_id: string | null
  bank_name?: string | null
  account_number?: string | null
}

type HoldRow = {
  id: string
  note: string | null
  customer_id: string | null
  customer_name: string | null
  cart_data: CartItem[]
  total_amount: number
  held_at: string
  status: string
}

type Session = {
  id: string
  status: string
  location_id: string
  opened_at: string
  user_id: string
  warehouses: { id: string; name: string } | null
}

type PaymentEntry = {
  tempId: string
  method: 'cash' | 'pos' | 'bank' | 'mobile_money' | 'credit' | 'advance'
  accountId: string
  accountName: string
  coaAccountId: string
  amount: string
  creditDays?: number
}

type SplitLine = {
  tempId: string
  method: 'cash' | 'bank' | 'pos' | 'mobile_money'
  accountId: string | null
  accountName: string
  amount: string
}


// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  return `${Math.floor(mins / 60)}h ago`
}

function stockColor(qty: number | null, reorder: number) {
  const q = qty ?? 0
  if (q <= 0) return 'out'
  if (q <= 5 || q <= reorder) return 'red'
  if (q <= 10) return 'amber'
  return 'green'
}


function itemDiscount(item: CartItem) {
  return item.discountType === 'percent'
    ? item.price * item.qty * (item.discountValue / 100)
    : item.discountValue
}

function itemLineTotal(item: CartItem) {
  return Math.max(0, item.price * item.qty - itemDiscount(item))
}

// Fix #4: the FINAL line total (for a bundle, totalSub === price*qty) before the
// Math.max(0,..) clamp. A discount that drives this below zero must be blocked —
// the clamp hides it on regular lines, but bundle component rows post the raw
// (negative) lineNet, which corrupts the sale journal. -0.005 tolerance lets a
// legitimate 100% discount / free line through.
function itemIsNegative(item: CartItem) {
  return item.price * item.qty - itemDiscount(item) < -0.005
}

// Upward-only price floor: a non-bundle cart line whose unit price sits below its
// catalogue floor is illegal (reductions must go through the permission-gated
// discount, not a lower price). cataloguePrice is the floor the client already
// computed at add-to-cart — the same LEAST(base, cheapest variant) the server
// enforces in post_sale_atomic_core (mig 322). 0.005 tolerance mirrors the other
// money guards; a 0 cataloguePrice or a bundle line never trips it.
function itemBelowFloor(item: CartItem) {
  return !item.bundleId && item.cataloguePrice > 0 && item.price < item.cataloguePrice - 0.005
}

function getCreditDueDate(weeks: number | null, customDate: string): string | null {
  if (customDate) return customDate
  if (!weeks) return null
  const d = new Date()
  d.setDate(d.getDate() + weeks * 7)
  return d.toISOString().slice(0, 10)
}

// ─── Calculator Popup ─────────────────────────────────────────────────────────
function CalculatorPopup({ onClose }: { onClose: () => void }) {
  const [display, setDisplay] = useState('0')
  const [prev, setPrev] = useState('')
  const [op, setOp] = useState('')
  const [fresh, setFresh] = useState(false)

  function press(val: string) {
    if (val === 'C') { setDisplay('0'); setPrev(''); setOp(''); setFresh(false); return }
    if (val === '←') { setDisplay(d => d.length > 1 ? d.slice(0, -1) : '0'); return }
    if (['+', '−', '×', '÷'].includes(val)) {
      setPrev(display); setOp(val); setFresh(true); return
    }
    if (val === '=') {
      const a = parseFloat(prev), b = parseFloat(display)
      let r = 0
      if (op === '+') r = a + b
      else if (op === '−') r = a - b
      else if (op === '×') r = a * b
      else if (op === '÷') r = b !== 0 ? a / b : 0
      setDisplay(String(parseFloat(r.toFixed(2))))
      setPrev(''); setOp(''); setFresh(false); return
    }
    if (val === '+/-') { setDisplay(d => String(-parseFloat(d))); return }
    setDisplay(d => {
      if (fresh) { setFresh(false); return val === '.' ? '0.' : val }
      if (val === '.' && d.includes('.')) return d
      return d === '0' && val !== '.' ? val : d + val
    })
  }

  const btns = ['C', '+/-', '←', '÷', '7', '8', '9', '×', '4', '5', '6', '−', '1', '2', '3', '+', '0', '.', '=']

  return (
    <div className="fixed bottom-20 right-4 z-50 bg-white rounded-2xl shadow-2xl p-4 w-60">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500">Calculator</span>
        <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
      </div>
      <div className="bg-gray-50 rounded-xl p-3 text-right text-2xl font-bold text-gray-900 mb-3 min-h-[2.5rem] break-all">
        {display}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {btns.map(b => (
          <button
            key={b}
            onClick={() => press(b)}
            className={cn(
              'h-10 rounded-xl text-sm font-semibold transition-colors',
              b === '=' ? 'col-span-2 bg-orange-500 text-white hover:bg-orange-600' :
              ['C', '+/-', '←'].includes(b) ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' :
              ['+', '−', '×', '÷'].includes(b) ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' :
              'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50'
            )}
          >
            {b}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Session Timer ────────────────────────────────────────────────────────────
function SessionTimer({ openedAt }: { openedAt: string }) {
  const computeSecs = () =>
    Math.max(0, Math.floor((Date.now() - new Date(openedAt).getTime()) / 1000))
  const [secs, setSecs] = useState(computeSecs)
  useEffect(() => {
    const t = setInterval(() => setSecs(computeSecs()), 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedAt])
  const hh = String(Math.floor(secs / 3600)).padStart(2, '0')
  const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0')
  const ss = String(secs % 60).padStart(2, '0')
  const over8h = secs >= 28800
  return (
    <span className={cn('text-xs font-semibold tabular-nums text-white inline-block', over8h && 'animate-pos-timer-buzz')}>
      <span className={cn('inline-block', over8h && 'animate-pos-timer-thump')}>
        <span className={cn(over8h && 'animate-pos-timer-glow')}>
          Open {hh}:{mm}:{ss}
        </span>
      </span>
    </span>
  )
}

// ─── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ product, cartQty, inCartTotal, onClick, onInc, onDec, isFlashing, isAtMax, locationStock, locationName, canEditProduct, onAddImage, isUploadingImage }: {
  product: ProductRow
  cartQty: number        // qty of the plain (non-variant) cart line for this product
  inCartTotal: number    // total qty across all lines (incl. variants) for this product
  onClick: () => void
  onInc: () => void
  onDec: () => void
  isFlashing?: boolean
  isAtMax?: boolean
  locationStock: number
  locationName: string
  canEditProduct: boolean
  onAddImage: (product: ProductRow) => void
  isUploadingImage: boolean
}) {
  const { format } = useCurrency()
  const stockQty = locationStock
  const stock = stockColor(stockQty, product.reorder_level)
  const isOutOfStock = stockQty <= 0
  const atStockMax = cartQty >= stockQty
  const cardRef = useRef<HTMLDivElement>(null)
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom')

  const handleMouseEnter = () => {
    if (!cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    setTooltipPosition(spaceBelow < 80 && spaceAbove > 80 ? 'top' : 'bottom')
  }

  return (
    <div className="relative group">
    {/* Card container = the tappable surface holding two stacked blocks (image
        float + details) with a small gap; the light-orange hover wash shows
        around and between them. Container is a div so the nested stepper /
        image-add buttons stay valid HTML; role/tabIndex/keydown keep it
        keyboard-operable. Card tap runs the same onClick (add-one / variant picker). */}
    <div
      ref={cardRef}
      role="button"
      tabIndex={isOutOfStock ? -1 : 0}
      aria-disabled={isOutOfStock}
      onMouseEnter={handleMouseEnter}
      onClick={isOutOfStock ? undefined : onClick}
      onKeyDown={isOutOfStock ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      className={cn(
        'flex flex-col gap-2 rounded-2xl p-1.5 transition-colors',
        isOutOfStock
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-orange-50 cursor-pointer',
        isFlashing && 'animate-pos-tile-flash'
      )}
    >
      {/* (a) Image float — fixed uniform box, white, whole product via object-contain */}
      <div className="relative w-full h-32 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center justify-center p-2 overflow-hidden">
        {/* Stock badge — corner pill */}
        <span
          aria-label={`${isOutOfStock ? 0 : stockQty} in stock${locationName ? ` at ${locationName}` : ''}`}
          className={cn(
            'absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none',
            isOutOfStock ? 'bg-red-500 text-white' :
            stock === 'red' ? 'bg-red-500 text-white' :
            stock === 'amber' ? 'bg-amber-400 text-amber-900' :
            'bg-green-100 text-green-700'
          )}
        >
          <Package2 className="h-3 w-3" aria-hidden="true" />
          {isOutOfStock ? 0 : stockQty}
        </span>

        {/* In-cart total badge — variant products in cart (no visible stepper) */}
        {cartQty === 0 && inCartTotal > 0 && (
          <div className="absolute top-1.5 right-1.5 z-10 h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center">
            {inCartTotal}
          </div>
        )}

        {/* Whole product on white, or letter-avatar fallback centered */}
        {product.image_url ? (
          <img src={product.image_url} alt="" className="max-h-full max-w-full object-contain pointer-events-none" />
        ) : (
          <span className="text-4xl font-bold text-orange-500 uppercase pointer-events-none">{product.name.charAt(0)}</span>
        )}

        {/* Inline image-add (admin/manager, image-less products only) */}
        {canEditProduct && !product.image_url && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAddImage(product) }}
            disabled={isUploadingImage}
            title="Add product image"
            aria-label={`Add image for ${product.name}`}
            className="absolute bottom-1.5 right-1.5 z-10 h-6 w-6 flex items-center justify-center rounded-full bg-white border border-orange-200 text-orange-500 shadow-sm hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:opacity-60"
          >
            {isUploadingImage
              ? <div className="h-3 w-3 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              : <ImagePlus className="h-3.5 w-3.5" />}
          </button>
        )}

        {/* Max-qty flash overlay */}
        {isAtMax && (
          <div className="absolute inset-0 bg-amber-400/10 rounded-xl flex items-center justify-center pointer-events-none">
            <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full border border-amber-300">Max qty</span>
          </div>
        )}
      </div>

      {/* (b) Details block — same width, left-aligned */}
      <div className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-2.5 text-left">
        <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 min-h-[2rem] mb-1">{product.name}</p>
        <p className="text-sm font-bold text-orange-600 mb-2">{format(product.selling_price)}</p>

        {/* Inline quantity control — reuses the existing add/qty logic. Neutral
            until hovered; stopPropagation so stepper clicks don't re-fire the
            card-tap add. Not in cart → Add (runs the same onClick path; variant
            products open the picker there). */}
        {!isOutOfStock && (
          cartQty > 0 ? (
            <div className="flex items-center justify-between gap-1 w-full" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDec() }}
                title="Remove one"
                className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-orange-500 hover:text-white hover:border-orange-500"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="text-sm font-bold text-gray-900 tabular-nums">{cartQty}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onInc() }}
                disabled={atStockMax}
                title="Add one"
                className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-orange-500 hover:text-white hover:border-orange-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-700 disabled:hover:border-gray-200"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClick() }}
              className="w-full h-8 flex items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-semibold shadow-sm transition-colors hover:bg-orange-500 hover:text-white hover:border-orange-500"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          )
        )}
      </div>
    </div>
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute left-1/2 z-50 hidden -translate-x-1/2 group-hover:block w-max max-w-[14rem] whitespace-normal break-words rounded-lg bg-gray-900 px-2.5 py-1.5 text-center text-xs font-medium text-white shadow-lg',
          tooltipPosition === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
        )}
      >
        {product.name}
      </span>
    </div>
  )
}

// ─── Bundle Card ──────────────────────────────────────────────────────────────
// Tile that sits in the product grid for an active bundle. Stock badge shows
// the floor-min component availability at the session's location; once that
// drops to 0 the tile becomes non-tappable, same as an out-of-stock product.
function BundleCard({ bundle, derivedPrice, availableQty, inCartQty, locationName, onClick, isAtMax }: {
  bundle: ActiveBundle
  derivedPrice: number
  availableQty: number
  inCartQty: number
  locationName: string
  onClick: () => void
  isAtMax: boolean
}) {
  const { format } = useCurrency()
  const isOutOfStock = availableQty <= 0
  return (
    <button
      onClick={isOutOfStock ? undefined : onClick}
      disabled={isOutOfStock}
      className={cn(
        'relative bg-white rounded-xl border text-left transition-all flex flex-col p-2.5 overflow-hidden min-h-[7.5rem]',
        isOutOfStock
          ? 'border-purple-100 opacity-50 cursor-not-allowed'
          : 'border-purple-200 hover:border-purple-400 hover:shadow-md active:scale-95 cursor-pointer',
      )}
    >
      {inCartQty > 0 && (
        <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-purple-500 text-white text-[9px] font-bold flex items-center justify-center">
          {inCartQty}
        </div>
      )}
      {isAtMax && (
        <div className="absolute inset-0 bg-amber-400/10 rounded-xl flex items-center justify-center">
          <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full border border-amber-300">Max qty</span>
        </div>
      )}
      <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center mb-1.5 flex-shrink-0">
        <Package2 className="h-4 w-4 text-purple-600" />
      </div>
      <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 flex-1 mb-1">{bundle.name}</p>
      <p className="text-xs font-bold text-purple-600">{format(derivedPrice)}</p>
      <div className="flex items-center gap-1 mt-0.5">
        <span className="text-[8px] font-bold uppercase tracking-wide px-1 py-px rounded-full bg-purple-100 text-purple-700">Bundle</span>
        <span
          aria-label={`${isOutOfStock ? 0 : availableQty} in stock${locationName ? ` at ${locationName}` : ''}`}
          className={cn(
            'inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none self-start',
            isOutOfStock ? 'bg-red-500 text-white' : availableQty <= 3 ? 'bg-amber-400 text-amber-900' : 'bg-green-100 text-green-700'
          )}
        >
          <Package2 className="h-3 w-3" aria-hidden="true" />
          {isOutOfStock ? 0 : availableQty}
        </span>
      </div>
    </button>
  )
}

// ─── Payment Account Modal ────────────────────────────────────────────────────
// Right-side panel that lists target accounts for a payment method, replicating
// the Split Payment panel's visual treatment so account selection is consistent
// across Cash, Bank and POS. Tapping a row applies the account and closes.
function PaymentAccountModal({ title, subtitle, accounts, selectedId, onSelect, onClose, emptyHint }: {
  title: string
  subtitle?: string
  accounts: PaymentAccount[]
  selectedId: string
  onSelect: (id: string) => void
  onClose: () => void
  emptyHint?: string
}) {
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col border-l">
      <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
        <div>
          <h3 className="font-bold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
        <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {accounts.length === 0 ? (
          <p className="text-xs text-gray-500 px-1 py-2">{emptyHint ?? 'No accounts set up. Go to Settings → Payment Accounts.'}</p>
        ) : accounts.map(acc => (
          <button
            key={acc.id}
            onClick={() => onSelect(acc.id)}
            className={cn(
              'w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-left transition-colors',
              selectedId === acc.id
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
            )}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{acc.name}</p>
              {acc.account_number && (
                <p className="text-xs text-gray-400">···{acc.account_number.slice(-4)}</p>
              )}
            </div>
            {selectedId === acc.id && <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Typeable Cart-Line Quantity ──────────────────────────────────────────────
// Mirrors the storefront QtyInput: keeps a local draft while editing so
// intermediate/empty values don't fight the cart. On blur/Enter the draft is
// parsed — a positive integer commits (min 1, capped at `max` when a stock
// ceiling applies); blank / 0 / non-numeric reverts to the current qty. The
// ▲/▼ steppers keep their existing behavior — ▼ at qty 1 removes the line via
// onChange(0) — so the same handler drives both the typed and stepped paths.
function QtyCell({ qty, max, onChange, label }: {
  qty: number
  max: number
  onChange: (n: number) => void
  label: string
}) {
  const [draft, setDraft] = useState(String(qty))
  useEffect(() => { setDraft(String(qty)) }, [qty])
  const atMax = Number.isFinite(max) && qty >= max
  const commit = () => {
    const n = parseInt(draft, 10)
    if (!Number.isNaN(n) && n >= 1) {
      const clamped = Number.isFinite(max) ? Math.min(n, max) : n
      onChange(clamped)
      setDraft(String(clamped))
    } else {
      setDraft(String(qty))
    }
  }
  return (
    <div className="flex items-center justify-center gap-0.5">
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={e => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
        aria-label={`Quantity for ${label}`}
        className="h-7 w-8 rounded-lg border border-gray-200 bg-white text-center text-xs font-bold text-gray-900 tabular-nums outline-none focus:ring-1 focus:ring-orange-400"
      />
      <div className="flex flex-col">
        <button
          onClick={() => { if (qty < max) onChange(qty + 1) }}
          disabled={atMax}
          title="Increase quantity"
          className="h-3 w-4 flex items-center justify-center text-gray-400 hover:text-orange-600 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronUp className="h-3 w-3" />
        </button>
        <button
          onClick={() => onChange(qty - 1)}
          title="Decrease quantity"
          className="h-3 w-4 flex items-center justify-center text-gray-400 hover:text-orange-600"
        >
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

// ─── Main POS Component ───────────────────────────────────────────────────────
export default function POSPage() {
  const qc = useQueryClient()
  const { format } = useCurrency()
  const { tenant, user } = useAuthStore()
  const { id: sessionId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const tid = tenant?.id ?? ''
  const displayName = user?.email?.split('@')[0] ?? 'Cashier'
  const { can } = usePermissions()
  const idem = useIdempotencyKey()
  // Online-order alert on the POS: cashiers work a focused screen where the
  // sidebar tab is easy to miss, so surface the same "needs action" count here.
  // Gated to staff who can open the Online Orders page (owners/managers auto;
  // cashiers once granted 'view') — a count they can't act on would only confuse.
  const { getLevel } = useCurrentUserPagePermissions()
  const canSeeOnlineOrders = getLevel('sell.online_orders') !== 'none'
  const { data: onlineKpis } = useOnlineOrderKpis(tid, canSeeOnlineOrders)
  const unattendedOnline = unattendedOnlineOrderCount(onlineKpis)
  const canApplyDiscount = can('pos.edit_discount')
  const canHeaderDiscount = can('sales.apply_discount')
  // Reuses the SAME gate as Inventory's row-thumbnail upload, so cashiers (who
  // lack products.edit) never see the POS tile image-add affordance.
  const canEditProduct = can('products.edit')

  // ── Session load ─────────────────────────────────────────────────────────
  const { data: session = null, isLoading: sessionLoading } = useQuery<Session | null>({
    queryKey: ['pos-session', sessionId],
    enabled: !!sessionId && !!tid,
    staleTime: 0,
    queryFn: async () => {
      const { data } = await db('pos_sessions')
        .select('id, tenant_id, user_id, location_id, opened_at, status, warehouses(id, name)')
        .eq('id', sessionId!)
        .eq('tenant_id', tid)
        .eq('status', 'open')
        .is('closed_at', null)
        .maybeSingle()
      return (data ?? null) as Session | null
    },
  })

  // ── Close session state ──────────────────────────────────────────────────
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [closingNotes, setClosingNotes] = useState('')
  const closeSession = useMutation({
    mutationFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('close_pos_session_atomic', {
        p_session_id: sessionId,
        p_closing_notes: closingNotes.trim() || null,
        p_closed_by: user?.id ?? null,
      })
      if (error) throw new Error(error.message ?? 'Failed to close session')
      await db('audit_logs').insert({
        tenant_id: tid,
        user_id: user?.id ?? null,
        action: 'update',
        resource_type: 'pos_sessions',
        resource_id: sessionId,
        description: `POS session closed at ${session?.warehouses?.name ?? ''}`,
        before_data: { status: 'open' },
        after_data: { status: 'closed' },
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos-open-sessions'] })
      qc.invalidateQueries({ queryKey: ['pos-sessions-history'] })
      navigate(`/pos/sessions/${sessionId}/report`, { replace: true })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // ── Live clock ──────────────────────────────────────────────────────────
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  // ── Cart state ──────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartItem[]>([])
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [customerName, setCustomerName] = useState<string | null>(null)

  // Sale-level (whole-invoice) discount — PR D/D3b. ₦ mode stores a plain amount;
  // % mode stores the entered rate (persisted to sale_discount_percent, NULL in ₦
  // mode). MUST be reset in clearCart() — POS is a long-lived component, so a
  // leftover discount would leak into the next customer's sale.
  const [saleDiscountType, setSaleDiscountType] = useState<'fixed' | 'percent'>('fixed')
  const [saleDiscountValue, setSaleDiscountValue] = useState(0)
  const [saleDiscountReason, setSaleDiscountReason] = useState('')

  // ── Search & filter ─────────────────────────────────────────────────────
  const [search, setSearch] = useState('')
  const [activeCat, setActiveCat] = useState<string>('all')
  const [showCatMenu, setShowCatMenu] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // ── Inline tile image-add (admin/manager) ────────────────────────────────
  // One hidden file input, scoped to whichever tile's ImagePlus was tapped
  // (held in pendingImgProduct) — mirrors the Inventory row-thumbnail pattern.
  const posImgFileRef = useRef<HTMLInputElement>(null)
  const pendingImgProduct = useRef<ProductRow | null>(null)
  const [uploadingImgId, setUploadingImgId] = useState<string | null>(null)

  // ── Customer inline search ──────────────────────────────────────────────
  const [custSearch, setCustSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)

  // ── Panels/modals ───────────────────────────────────────────────────────
  const [showCalc, setShowCalc] = useState(false)
  const [showHolds, setShowHolds] = useState(false)
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [qaName, setQaName] = useState('')
  const [qaPhone, setQaPhone] = useState('')
  const [qaBusinessName, setQaBusinessName] = useState('')
  const [qaEmail, setQaEmail] = useState('')
  const [qaCreditLimit, setQaCreditLimit] = useState('')
  const [showSuspendPrompt, setShowSuspendPrompt] = useState(false)
  const [suspendNote, setSuspendNote] = useState('')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState<{ orderNumber: string; total: number; change: number } | null>(null)
  const [flashingId, setFlashingId] = useState<string | null>(null)
  const [flashingMaxId, setFlashingMaxId] = useState<string | null>(null)
  const [showLocationDropdown] = useState(false)
  void showLocationDropdown
  const [receiptData, setReceiptData] = useState<{
    orderNumber: string
    items: Array<CartItem & { total: number }>
    subtotal: number
    discount: number
    headerDiscount: number
    headerDiscountReason: string | null
    tax: number
    total: number
    change: number
    cashierName: string
    registerName: string
    customerName: string | null
    customerId: string | null
    customerPhone: string | null
    customerAddress: string | null
    amountPaid: number
    nowOwing: number | null
    paymentMethod: string
    timestamp: Date
  } | null>(null)
  // Tenant receipt add-ons (custom header/footer text + balance toggle), from tenants.settings.
  const receiptExtras = readReceiptExtras(tenant?.settings)

  // ── Variant picker ──────────────────────────────────────────────────────
  const [variantProduct, setVariantProduct] = useState<ProductRow | null>(null)
  const { data: variantOptions = [], isFetching: fetchingVariants } = useQuery<{ id: string; name: string; selling_price: number; quantity: number }[]>({
    queryKey: ['product-variants-pos', variantProduct?.id],
    enabled: !!variantProduct,
    queryFn: async () => {
      const { data } = await db('product_variants').select('id,name,selling_price,quantity').eq('product_id', variantProduct!.id).order('name')
      return (data ?? []) as { id: string; name: string; selling_price: number; quantity: number }[]
    },
  })

  useEffect(() => {
    if (variantProduct && !fetchingVariants && variantOptions.length === 0) {
      addToCart(variantProduct)
      setVariantProduct(null)
    }
  }, [variantProduct, fetchingVariants, variantOptions.length])

  // ── Payment tab state ───────────────────────────────────────────────────
  const [payTab, setPayTab] = useState<'cash' | 'bank' | 'pos' | 'credit' | 'multiple' | null>(null)
  const [selectedCashId, setSelectedCashId] = useState<string>('')
  const [selectedBankId, setSelectedBankId] = useState<string>('')
  const [selectedPosId, setSelectedPosId] = useState<string>('')
  const [cashInput, setCashInput] = useState('')
  const [bankInput, setBankInput] = useState('')
  const [posInput, setPosInput] = useState('')
  // Dirty = the cashier has manually edited this tab's amount, so the reactive
  // autofill must leave it alone. Autofill clears it; each input's onChange sets it.
  const [cashDirty, setCashDirty] = useState(false)
  const [bankDirty, setBankDirty] = useState(false)
  const [posDirty, setPosDirty] = useState(false)
  const [creditWeeks, setCreditWeeks] = useState<number | null>(null)
  const [creditCustomDate, setCreditCustomDate] = useState('')
  const [showSplitPanel, setShowSplitPanel] = useState(false)
  const [splitLines, setSplitLines] = useState<SplitLine[]>([])
  const [splitAdvance, setSplitAdvance] = useState('')
  const [accountModal, setAccountModal] = useState<'cash' | 'bank' | 'pos' | null>(null)

  // ─── Queries ──────────────────────────────────────────────────────────────
  const { data: categories = [] } = useQuery<CategoryRow[]>({
    queryKey: ['pos-categories', tid],
    enabled: !!tid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await db('product_categories').select('id,name,color').eq('tenant_id', tid).order('name')
      return data ?? []
    },
  })

  // The first 4 categories render as inline pills; the rest live under an "Other
  // categories" dropdown. This flags when the active category is one of those
  // overflow ones so the dropdown button can show its name + active styling.
  const overflowActiveCat = categories.slice(4).find(c => c.id === activeCat) ?? null

  // Product grid is server-driven so every product stays reachable (no client
  // 1000-row cap). Modes, evaluated per render:
  //   • typed search (>=2 chars) → server name/SKU ilike, capped at 50
  //   • a category selected      → all products in that category, no cap
  //   • default ("all" + no search) → all active products, no cap
  // The active category, when set, also scopes a typed search (prior behaviour).
  // Barcode scans resolve via a direct lookup in the scan handler, not here.
  const productSearch = search.trim()
  const searchActive = productSearch.length >= 2
  const { data: products = [], isLoading: loadingProducts, refetch: refetchProducts } = useQuery<ProductRow[]>({
    queryKey: ['pos-products', tid, activeCat, searchActive ? productSearch : ''],
    enabled: !!tid,
    staleTime: 2 * 60_000,
    queryFn: async () => {
      let q = db('products')
        .select('id,name,sku,barcode,selling_price,cost_price,reorder_level,reorder_point,is_active,image_url,quantity,category_id,enable_lot_tracking,enable_expiry_tracking,product_categories(id,name,color),vat_categories(is_vatable)')
        .eq('tenant_id', tid)
        .eq('is_active', true)
      if (activeCat !== 'all') q = q.eq('category_id', activeCat)
      if (searchActive) {
        q = q.or(`name.ilike.%${productSearch}%,sku.ilike.%${productSearch}%`).order('name').limit(50)
      } else {
        q = q.order('name')
      }
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as ProductRow[]
    },
  })

  // Cheap existence check — distinguishes "tenant has no products yet"
  // (onboarding hint) from "current search/category returned nothing".
  const { data: hasAnyProducts = true } = useQuery({
    queryKey: ['pos-has-products', tid],
    enabled: !!tid,
    staleTime: 2 * 60_000,
    queryFn: async () => {
      const { data } = await db('products')
        .select('id').eq('tenant_id', tid).eq('is_active', true).limit(1)
      return (data?.length ?? 0) > 0
    },
  })

  const { data: businessTax } = useBusinessTaxSettings(tid)
  const vatRate = businessTax?.vat_rate ?? 7.5

  // Per-location stock for this POS session's warehouse
  const sessionLocationId = session?.location_id ?? ''
  const { data: locationStockRows = [] } = useQuery<Array<{ product_id: string; quantity: number }>>({
    queryKey: ['pos-current-stock', tid, sessionLocationId],
    enabled: !!tid && !!sessionLocationId,
    queryFn: async () => {
      const { data } = await db('current_stock')
        .select('product_id, quantity')
        .eq('tenant_id', tid)
        .eq('warehouse_id', sessionLocationId)
      return (data ?? []) as Array<{ product_id: string; quantity: number }>
    },
  })
  const locationStockMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const s of locationStockRows) map[s.product_id] = s.quantity ?? 0
    return map
  }, [locationStockRows])
  const sessionLocationName = session?.warehouses?.name ?? ''
  function locationStockFor(productId: string): number {
    return locationStockMap[productId] ?? 0
  }

  // Active bundles for this tenant. Availability per location derives from
  // locationStockMap; the recipe itself is global.
  const { data: activeBundles = [] } = useActiveBundlesForLocation({ tenantId: tid, locationId: sessionLocationId })

  const { data: paymentAccounts = [] } = useQuery<PaymentAccount[]>({
    queryKey: ['receiving-accounts-full', tid],
    enabled: !!tid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const accounts = await getReceivingAccounts(supabase, tid)
      return accounts.map(a => ({
        id: a.id,
        name: a.name,
        type: detectAccountType(a.name),
        account_type: detectAccountType(a.name),
        current_balance: undefined,
        coa_account_id: a.id,
        bank_name: null,
        account_number: null,
      })) as PaymentAccount[]
    },
  })

  const { data: custResults = [] } = useQuery<CustomerRow[]>({
    queryKey: ['pos-customers', tid, custSearch],
    enabled: !!tid && custSearch.length >= 2,
    queryFn: async () => {
      const { data } = await db('customers')
        .select('id,name,business_name,phone,outstanding_balance,credit_limit')
        .eq('tenant_id', tid)
        .or(`name.ilike.%${custSearch}%,phone.ilike.%${custSearch}%,business_name.ilike.%${custSearch}%,email.ilike.%${custSearch}%`)
        .limit(10)
      return (data ?? []) as CustomerRow[]
    },
  })

  const { data: holds = [], refetch: refetchHolds } = useQuery<HoldRow[]>({
    queryKey: ['pos-holds', tid],
    enabled: !!tid,
    queryFn: async () => {
      const { data } = await db('pos_holds').select('*').eq('tenant_id', tid).eq('status', 'held').order('held_at', { ascending: false })
      return (data ?? []) as HoldRow[]
    },
  })


  const { data: tenantUserProfile } = useQuery<{ username: string | null; full_name: string | null; first_name: string | null; last_name: string | null } | null>({
    queryKey: ['tenant-user-profile', tid, user?.id],
    enabled: !!tid && !!user?.id,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await (db('tenant_users') as any)
        .select('username, full_name, first_name, last_name')
        .eq('tenant_id', tid)
        .eq('user_id', user!.id)
        .maybeSingle()
      return (data ?? null) as { username: string | null; full_name: string | null; first_name: string | null; last_name: string | null } | null
    },
  })

  const cashierName =
    tenantUserProfile?.username ||
    tenantUserProfile?.full_name ||
    (tenantUserProfile?.first_name && tenantUserProfile?.last_name
      ? `${tenantUserProfile.first_name} ${tenantUserProfile.last_name}`
      : null) ||
    user?.full_name ||
    user?.email?.split('@')[0] ||
    'Cashier'

  // Advance the checkout may APPLY. Sourced from the guard-identical journal reader
  // (contact_type='customer' net, GREATEST(0,−net)) — NOT the denormalized
  // customers.outstanding_balance, which drifts and produced a phantom advance the
  // post_sale_atomic guard then rejected ("exceeds available advance").
  const { data: availableDeposit = 0 } = useQuery<number>({
    queryKey: ['customer-advance-pos', tid, customerId],
    enabled: !!tid && !!customerId,
    queryFn: async () => {
      if (!customerId) return 0
      return await getAvailableCustomerAdvance(supabase, tid, customerId)
    },
  })

  // Credit-limit soft-warning data: pull the selected customer's limit and
  // journal-derived AR balance (same source as Contacts / NewSalePage). The
  // outstanding here is clamped to >= 0 — a negative AR (customer credit) is
  // treated as zero owed for the limit check.
  const { data: customerCreditInfo } = useQuery<{ credit_limit: number; outstanding: number; display_name: string } | null>({
    queryKey: ['pos-customer-credit-info', tid, customerId],
    enabled: !!tid && !!customerId,
    queryFn: async () => {
      if (!customerId) return null
      const { data: cust } = await db('customers')
        .select('name,business_name,credit_limit')
        .eq('tenant_id', tid)
        .eq('id', customerId)
        .maybeSingle()
      const bal = await getCustomerBalance(supabase, tid, customerId)
      return {
        credit_limit: cust?.credit_limit ?? 0,
        outstanding: bal > 0 ? bal : 0,
        display_name: cust?.business_name || cust?.name || 'this customer',
      }
    },
  })

  // ── Grouped accounts ────────────────────────────────────────────────────
  const accountsByType = useMemo(() => {
    const map: Record<string, PaymentAccount[]> = {}
    paymentAccounts.forEach(a => {
      const t = a.type || ''
      if (!map[t]) map[t] = []
      map[t].push(a)
    })
    return map
  }, [paymentAccounts])

  const cashAccounts = useMemo(() =>
    paymentAccounts.filter(a => a.type === 'cash'),
    [paymentAccounts])
  const posAccounts = useMemo(() =>
    paymentAccounts.filter(a => a.type === 'pos_machine' || a.type === 'pos'),
    [paymentAccounts])
  const mobileAccounts = useMemo(() =>
    paymentAccounts.filter(a => a.type === 'mobile_money'),
    [paymentAccounts])
  // Bank is the catch-all bucket: every receiving account that isn't clearly
  // cash or a POS terminal lands here — including 'other' accounts the name
  // classifier couldn't tag (e.g. "PalmPay", "Opay"). Without this they were
  // typed 'other' and rendered under NO tab, so they were unpickable. That was
  // the real cause of "only 2 banks show", not any slice/limit on the pills.
  const bankAccounts = useMemo(() =>
    paymentAccounts.filter(a => a.type !== 'cash' && a.type !== 'pos_machine' && a.type !== 'pos'),
    [paymentAccounts])

  // ── All payment options (for split panel) ───────────────────────────────
  const allPaymentOptions = useMemo(() => {
    const opts: Array<{ label: string; method: SplitLine['method']; accountId: string | null }> = []
    cashAccounts.forEach(a => opts.push({ label: a.name, method: 'cash', accountId: a.id }))
    bankAccounts.forEach(a => opts.push({ label: a.name, method: 'bank', accountId: a.id }))
    posAccounts.forEach(a => opts.push({ label: a.name, method: 'pos', accountId: a.id }))
    mobileAccounts.forEach(a => opts.push({ label: a.name, method: 'mobile_money', accountId: a.id }))
    // bankAccounts is a catch-all that now also contains mobile-money accounts,
    // so the same account can be pushed twice — keep the first occurrence only.
    const seen = new Set<string>()
    return opts.filter(o => {
      const k = o.accountId ?? ''
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }, [cashAccounts, bankAccounts, posAccounts, mobileAccounts])

  // Bundles only show when the "all" category is selected (bundles aren't
  // categorised) and either no search or the bundle name matches the search.
  const filteredBundles = useMemo(() => {
    if (activeCat !== 'all') return []
    if (!search) return activeBundles
    const q = search.toLowerCase()
    return activeBundles.filter(b => b.name.toLowerCase().includes(q))
  }, [activeBundles, activeCat, search])

  // When a payment tab has exactly one target account, select it for the
  // cashier so they don't have to tap a lone pill on every sale.
  useEffect(() => {
    if (payTab === 'cash' && cashAccounts.length === 1 && !selectedCashId) setSelectedCashId(cashAccounts[0].id)
    if (payTab === 'bank' && bankAccounts.length === 1 && !selectedBankId) setSelectedBankId(bankAccounts[0].id)
    if (payTab === 'pos' && posAccounts.length === 1 && !selectedPosId) setSelectedPosId(posAccounts[0].id)
  }, [payTab, cashAccounts, bankAccounts, posAccounts, selectedCashId, selectedBankId, selectedPosId])

  // ── Computed cart totals ─────────────────────────────────────────────────
  const cartItemsWithTotals = useMemo(() => cart.map(i => ({ ...i, total: itemLineTotal(i) })), [cart])
  const totalDiscount = cart.reduce((s, i) => s + itemDiscount(i), 0)
  const subtotal = cartItemsWithTotals.reduce((s, i) => s + i.total, 0)

  // Whole-invoice discount (PR D/D3b). % mode → % of subtotal (net of line
  // discounts). Gated on canHeaderDiscount so a user without the permission can
  // never carry a discount even if state was somehow set.
  const headerDiscount = canHeaderDiscount ? resolveHeaderDiscountAmount(saleDiscountType, saleDiscountValue, subtotal) : 0
  // Per-item allocation of the header discount (proportional to each item's net
  // total, summing EXACTLY to headerDiscount, remainder → largest). Keyed by the
  // stable cart-item id; consumed by totalVat, grandTotal, and the payload.
  const headerAllocation = allocateHeaderDiscount(cartItemsWithTotals.map(i => ({ key: i.id, base: i.total })), headerDiscount)
  // Client mirror of the mig-274 server guards (min=0 enforced on the input).
  const headerDiscountError = headerDiscount <= 0
    ? null
    : subtotal <= 0.005
      ? 'Cannot apply a discount to a zero-subtotal sale'
      : headerDiscount > subtotal + 0.005
        ? `Discount ${format(headerDiscount)} cannot exceed subtotal ${format(subtotal)}`
        : null

  // VAT: plain items use their isVatable flag. Bundle items VAT is the sum
  // of per-component VAT (each component's own is_vatable flag), with the
  // line discount AND the header-discount allocation split proportionally across
  // components (the header split uses the shared helper so summary VAT === payload VAT).
  const totalVat = cartItemsWithTotals.reduce((s, i) => {
    const alloc = headerAllocation.get(i.id) ?? 0
    if (i.bundleId && i.components) {
      const subs = i.components.map(c => i.qty * c.unit_qty * c.unit_price)
      const totalSub = subs.reduce((x, n) => x + n, 0)
      if (totalSub <= 0) return s
      const lineDisc = itemDiscount(i)
      const headerShares = allocateProportional(subs, alloc)
      let vat = 0
      for (let j = 0; j < i.components.length; j++) {
        const c = i.components[j]
        if (!c.is_vatable) continue
        const taxable = subs[j] - lineDisc * (subs[j] / totalSub) - headerShares[j]
        vat += taxable * vatRate / 100
      }
      return s + vat
    }
    return s + (i.isVatable ? (i.total - alloc) * vatRate / 100 : 0)
  }, 0)
  // MUST subtract headerDiscount explicitly: subtotal stays gross-of-header and
  // only totalVat drops with the reduced base, so omitting this term would
  // silently overcharge the customer by the discount amount.
  const grandTotal = subtotal - headerDiscount + totalVat

  // Re-sync a still-autofilled amount when grandTotal changes (e.g. a discount is
  // applied after the tab was picked). Skips manually-edited (dirty) and empty
  // fields, and only touches the active tab. Deliberately keyed on grandTotal/payTab
  // only — adding the input to deps would fight the cashier's own typing.
  useEffect(() => {
    if (grandTotal <= 0) return
    const s = String(grandTotal)
    if (payTab === 'cash' && !cashDirty && cashInput !== '' && cashInput !== s) setCashInput(s)
    else if (payTab === 'bank' && !bankDirty && bankInput !== '' && bankInput !== s) setBankInput(s)
    else if (payTab === 'pos' && !posDirty && posInput !== '' && posInput !== s) setPosInput(s)
  }, [grandTotal, payTab])

  // ── Effective payments (computed from current tab state) ─────────────────
  const effectivePayments = useMemo((): PaymentEntry[] => {
    if (!payTab) return []

    if (payTab === 'cash') {
      const acc = cashAccounts.find(a => a.id === selectedCashId)
      if (!acc) return []
      return [{
        tempId: 'cash-1', method: 'cash',
        accountId: acc.id, accountName: acc.name,
        coaAccountId: acc.coa_account_id ?? '',
        amount: cashInput || String(grandTotal),
      }]
    }

    if (payTab === 'bank') {
      const acc = bankAccounts.find(a => a.id === selectedBankId)
      if (!acc) return []
      return [{
        tempId: 'bank-1', method: 'bank',
        accountId: acc.id, accountName: acc.name,
        coaAccountId: acc.coa_account_id ?? '',
        amount: bankInput || String(grandTotal),
      }]
    }

    if (payTab === 'pos') {
      const acc = posAccounts.find(a => a.id === selectedPosId)
      if (!acc) return []
      return [{
        tempId: 'pos-1', method: 'pos',
        accountId: acc.id, accountName: acc.name,
        coaAccountId: acc.coa_account_id ?? '',
        amount: posInput || String(grandTotal),
      }]
    }

    if (payTab === 'credit') {
      return [{
        tempId: 'credit-1', method: 'credit',
        accountId: '', accountName: 'Credit',
        coaAccountId: '', amount: String(grandTotal),
      }]
    }

    if (payTab === 'multiple') {
      const lines: PaymentEntry[] = splitLines
        .filter(l => parseFloat(l.amount) > 0)
        .map(l => {
          const allAccounts = [...bankAccounts, ...posAccounts, ...mobileAccounts]
          const acc = allAccounts.find(a => a.id === l.accountId)
          return {
            tempId: l.tempId,
            method: l.method,
            accountId: l.accountId ?? '',
            accountName: l.accountName,
            coaAccountId: acc?.coa_account_id ?? '',
            amount: l.amount,
          }
        })
      const advAmt = parseFloat(splitAdvance) || 0
      if (advAmt > 0) {
        lines.push({
          tempId: 'advance-split', method: 'advance',
          accountId: '', accountName: 'Customer Advance',
          coaAccountId: '', amount: splitAdvance,
        })
      }
      return lines
    }

    return []
  }, [payTab, cashAccounts, bankAccounts, posAccounts, mobileAccounts, cashInput, bankInput, posInput, selectedCashId, selectedBankId, selectedPosId, splitLines, splitAdvance, grandTotal])

  const totalPaid = effectivePayments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
  const change = Math.max(0, totalPaid - grandTotal)
  const balanceDue = Math.max(0, grandTotal - totalPaid)

  // Credit portion = portion of this sale that will become new receivable.
  // Mirrors submitSale: grandTotal − (cash/bank/pos lines) − advance used.
  // Only meaningful when a credit-bearing tab is active (Credit or Split).
  const creditPortion = useMemo(() => {
    if (payTab !== 'credit' && payTab !== 'multiple') return 0
    const cashLinesSum = effectivePayments
      .filter(p => p.method !== 'credit' && p.method !== 'advance')
      .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
    const advanceUsed = parseFloat(effectivePayments.find(p => p.method === 'advance')?.amount ?? '0') || 0
    return Math.max(0, grandTotal - cashLinesSum - advanceUsed)
  }, [payTab, effectivePayments, grandTotal])

  // Split panel totals
  const splitAdvanceNum = parseFloat(splitAdvance) || 0
  const splitLinesPaid = splitLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0)
  const splitTotalPaid = splitLinesPaid + splitAdvanceNum
  const splitRemaining = grandTotal - splitTotalPaid

  // ── Cart actions ────────────────────────────────────────────────────────
  const addToCart = useCallback((product: ProductRow, variantId?: string, variantName?: string, variantPrice?: number) => {
    const itemId = variantId ? `${product.id}__${variantId}` : product.id
    const name = variantName ? `${product.name} - ${variantName}` : product.name
    const price = variantPrice ?? product.selling_price
    const maxQty = locationStockFor(product.id)
    const isVatable = product.vat_categories?.is_vatable ?? false
    setCart(prev => {
      const existing = prev.find(i => i.id === itemId)
      if (existing) {
        if (existing.qty >= maxQty) return prev
        return prev.map(i => i.id === itemId ? { ...i, qty: i.qty + 1, total: itemLineTotal({ ...i, qty: i.qty + 1 }) } : i)
      }
      if (maxQty <= 0) return prev
      return [...prev, { id: itemId, productId: product.id, variantId: variantId ?? null, name, sku: product.sku, price, cataloguePrice: price, qty: 1, discountType: 'fixed', discountValue: 0, isVatable, total: price, imageUrl: product.image_url }]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationStockMap])

  // Add an active bundle to the cart. Components is the snapshot from
  // useActiveBundlesForLocation — editing the bundle later will NOT mutate
  // this cart entry or the eventual sale rows.
  const addBundleToCart = useCallback((bundle: ActiveBundle) => {
    const itemId = `bundle:${bundle.id}`
    const derivedPrice = bundleDerivedPrice(bundle)
    const availableQty = bundleAvailableQty(bundle, locationStockMap)
    if (availableQty <= 0) {
      toast.error(`${bundle.name} cannot be assembled at ${sessionLocationName || 'this location'} — one or more components are out of stock.`)
      return
    }
    setCart(prev => {
      const existing = prev.find(i => i.id === itemId)
      if (existing) {
        if (existing.qty >= availableQty) return prev
        return prev.map(i => i.id === itemId ? { ...i, qty: i.qty + 1, total: itemLineTotal({ ...i, qty: i.qty + 1 }) } : i)
      }
      return [...prev, {
        id: itemId,
        productId: '',
        variantId: null,
        name: bundle.name,
        sku: null,
        price: derivedPrice,
        cataloguePrice: derivedPrice,
        qty: 1,
        discountType: 'fixed',
        discountValue: 0,
        // Bundle line VAT is computed per-component; the line-level isVatable
        // flag is unused for bundles. Set false to keep the type happy.
        isVatable: false,
        total: derivedPrice,
        imageUrl: null,
        bundleId: bundle.id,
        bundleName: bundle.name,
        components: bundle.components,
      }]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationStockMap, sessionLocationName])

  function updateQty(itemId: string, qty: number) {
    if (qty <= 0) { setCart(prev => prev.filter(i => i.id !== itemId)); return }
    setCart(prev => prev.map(i => i.id === itemId ? { ...i, qty, total: itemLineTotal({ ...i, qty }) } : i))
  }

  function updateItemDiscount(itemId: string, type: 'fixed' | 'percent', value: number) {
    setCart(prev => prev.map(i => i.id === itemId ? { ...i, discountType: type, discountValue: value, total: itemLineTotal({ ...i, discountType: type, discountValue: value }) } : i))
  }

  // Upward-only unit price. Raising is free; the value is clamped back to the
  // catalogue floor on blur (see the cart-line input) so state can dip below only
  // transiently while typing. Recompute the line total from the new price.
  function updateItemPrice(itemId: string, price: number) {
    setCart(prev => prev.map(i => i.id === itemId ? { ...i, price, total: itemLineTotal({ ...i, price }) } : i))
  }

  function clearCart() {
    setCart([])
    setCustomerId(null)
    setCustomerName(null)
    setCustSearch('')
    setPayTab(null)
    setCashInput('')
    setBankInput('')
    setPosInput('')
    setCashDirty(false)
    setBankDirty(false)
    setPosDirty(false)
    setSelectedCashId('')
    setSelectedBankId('')
    setSelectedPosId('')
    setCreditWeeks(null)
    setCreditCustomDate('')
    setSplitLines([])
    setSplitAdvance('')
    setShowSplitPanel(false)
    setAccountModal(null)
    // Reset the whole-invoice discount — POS is long-lived, so without this a
    // discount would leak into the next customer's sale.
    setSaleDiscountType('fixed')
    setSaleDiscountValue(0)
    setSaleDiscountReason('')
  }

  // ── Pay tab selection ───────────────────────────────────────────────────
  function selectPayTab(tab: 'cash' | 'bank' | 'pos' | 'credit' | 'multiple') {
    if (tab === 'credit' && !customerId) {
      toast.error('Select a customer to enable credit sales')
      return
    }
    setPayTab(tab)
    // Reset any previously chosen account so the cashier picks again every sale.
    setSelectedCashId('')
    setSelectedBankId('')
    setSelectedPosId('')
    if (tab === 'multiple') {
      setShowSplitPanel(true)
      if (splitLines.length === 0) {
        setSplitLines([{
          tempId: Math.random().toString(36).slice(2),
          method: 'cash',
          accountId: null,
          accountName: '',
          amount: '',
        }])
      }
    } else {
      setShowSplitPanel(false)
      // Pre-fill amounts with grand total. Clearing the dirty flag marks the value
      // as autofilled so the reactive effect may keep it in sync with grandTotal.
      if (tab === 'cash' && !cashInput) { setCashInput(grandTotal > 0 ? String(grandTotal) : ''); setCashDirty(false) }
      if (tab === 'bank' && !bankInput) { setBankInput(grandTotal > 0 ? String(grandTotal) : ''); setBankDirty(false) }
      if (tab === 'pos' && !posInput) { setPosInput(grandTotal > 0 ? String(grandTotal) : ''); setPosDirty(false) }
      // Account chooser opens as a side modal (same pattern as Split). A lone
      // account auto-selects via effect, so only open the picker for 2+.
      if (tab === 'cash' && cashAccounts.length > 1) setAccountModal('cash')
      else if (tab === 'bank' && bankAccounts.length > 1) setAccountModal('bank')
      else if (tab === 'pos' && posAccounts.length > 1) setAccountModal('pos')
    }
  }

  // ── Product click ───────────────────────────────────────────────────────
  const handleProductClick = useCallback((product: ProductRow) => {
    const avail = locationStockFor(product.id)
    if (avail <= 0) {
      toast.error(`${product.name} has 0 stock at ${sessionLocationName || 'this location'}. Transfer stock here to sell it.`)
      return
    }
    const currentQty = cart.find(i => i.productId === product.id)?.qty ?? 0
    if (currentQty >= avail) {
      setFlashingMaxId(product.id)
      setTimeout(() => setFlashingMaxId(null), 1000)
      return
    }
    setFlashingId(product.id)
    setTimeout(() => setFlashingId(null), 280)
    setVariantProduct(product)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, locationStockMap, sessionLocationName])

  // ── Inline tile image-add ─────────────────────────────────────────────────
  function openPosImagePicker(product: ProductRow) {
    if (!canEditProduct) return
    pendingImgProduct.current = product
    posImgFileRef.current?.click()
  }

  async function handlePosImagePick(file: File | undefined) {
    const p = pendingImgProduct.current
    pendingImgProduct.current = null
    if (posImgFileRef.current) posImgFileRef.current.value = ''
    if (!file || !p) return
    if (!canEditProduct) { toast.error('Not authorized to edit product'); return }
    setUploadingImgId(p.id)
    try {
      const url = await optimizeAndUploadProductImage(file, tid)
      const { error } = await db('products').update({ image_url: url }).eq('id', p.id).eq('tenant_id', tid)
      if (error) throw new Error(error.message)
      toast.success('Image updated')
      refetchProducts()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update image')
    } finally {
      setUploadingImgId(null)
    }
  }

  // ── Barcode scan ────────────────────────────────────────────────────────
  const [barcodeBuffer, setBarcodeBuffer] = useState('')
  const barcodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'Enter' && barcodeBuffer) {
        const code = barcodeBuffer
        setBarcodeBuffer('')
        // Direct server lookup by exact SKU or barcode — no character gate, no
        // limit-50, fetch just the matching row so any product is scannable.
        void (async () => {
          const { data } = await db('products')
            .select('id,name,sku,barcode,selling_price,cost_price,reorder_level,reorder_point,is_active,image_url,quantity,category_id,enable_lot_tracking,enable_expiry_tracking,product_categories(id,name,color),vat_categories(is_vatable)')
            .eq('tenant_id', tid).eq('is_active', true)
            .or(`sku.eq.${code},barcode.eq.${code}`)
            .limit(1)
          const found = (data?.[0] ?? null) as ProductRow | null
          if (found) { handleProductClick(found); toast.success(`${found.name} added`, { duration: 1000 }) }
          else toast.error(`No product: ${code}`)
        })()
        return
      }
      if (e.key.length === 1) {
        setBarcodeBuffer(b => b + e.key)
        if (barcodeTimer.current) clearTimeout(barcodeTimer.current)
        barcodeTimer.current = setTimeout(() => setBarcodeBuffer(''), 300)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [tid, barcodeBuffer, handleProductClick])

  // ── Mutations ─────────────────────────────────────────────────────────────
  const quickAddCustomer = useMutation({
    mutationFn: async () => {
      if (!qaName.trim()) throw new Error('Contact name is required')
      if (!qaPhone.trim()) throw new Error('Phone number is required')
      const { data, error } = await db('customers').insert({
        tenant_id: tid,
        name: qaName.trim(),
        business_name: qaBusinessName.trim() || null,
        phone: qaPhone.trim(),
        email: qaEmail.trim() || null,
        credit_limit: parseFloat(qaCreditLimit) || 0,
        outstanding_balance: 0,
      }).select('id, name, business_name, phone, outstanding_balance').single()
      if (error) {
        console.error('Add customer error:', error)
        throw new Error(error.message)
      }
      return data as CustomerRow
    },
    onSuccess: (c) => {
      const displayNameCustomer = c.business_name || c.name
      setCustomerId(c.id)
      setCustomerName(displayNameCustomer)
      setShowQuickAdd(false)
      setQaName(''); setQaPhone(''); setQaBusinessName(''); setQaEmail(''); setQaCreditLimit('')
      qc.invalidateQueries({ queryKey: ['pos-customers'] })
      toast.success(`${displayNameCustomer} added`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const suspendSale = useMutation({
    mutationFn: async () => {
      if (cart.length === 0) throw new Error('Cart is empty')
      const heldAt = new Date()
      const timeStr = heldAt.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })
      const label = `Hold · ${customerName ?? 'Walk-in'} · ${timeStr}`
      const { error } = await db('pos_holds').insert({
        tenant_id: tid, customer_id: customerId ?? null,
        customer_name: customerName ?? null, cart_data: cart,
        total_amount: grandTotal, held_by: user?.id ?? null,
        held_at: heldAt.toISOString(), register_id: null,
        note: suspendNote.trim() || null, status: 'held',
        label,
      })
      if (error) throw error
    },
    onSuccess: () => {
      clearCart(); setSuspendNote(''); setShowSuspendPrompt(false)
      refetchHolds(); toast.success('Sale suspended')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const resumeHold = useMutation({
    mutationFn: async (hold: HoldRow) => {
      await db('pos_holds').update({ status: 'resumed', resumed_at: new Date().toISOString() }).eq('id', hold.id)
      return hold
    },
    onSuccess: (hold) => {
      clearCart()
      ;(hold.cart_data ?? []).forEach(item => {
        // Backfill cataloguePrice for holds saved before the upward-only-price
        // change (older cart_data lacks it) so the floor is never undefined.
        const restored = { ...item, cataloguePrice: item.cataloguePrice ?? item.price }
        setCart(prev => {
          const exists = prev.find(i => i.id === restored.id)
          if (exists) return prev.map(i => i.id === restored.id ? { ...i, qty: i.qty + restored.qty, total: itemLineTotal({ ...i, qty: i.qty + restored.qty }) } : i)
          return [...prev, restored]
        })
      })
      setCustomerId(hold.customer_id)
      setCustomerName(hold.customer_name)
      // NOTE: held sales do not carry a whole-invoice discount — pos_holds stores
      // only cart_data, so a resumed hold starts with no header discount. Adding
      // one to holds would need a pos_holds schema change (out of D3b scope).
      setShowHolds(false)
      refetchHolds()
      toast.success('Sale resumed')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const deleteHold = useMutation({
    mutationFn: async (id: string) => { await db('pos_holds').update({ status: 'deleted' }).eq('id', id) },
    onSuccess: () => { refetchHolds(); toast.success('Hold deleted') },
  })

  const submitSale = useMutation({
    mutationFn: async () => {
      if (!session) throw new Error('No active POS session')
      if (cart.length === 0) throw new Error('Cart is empty')
      if (cart.some(itemIsNegative)) throw new Error('A line discount cannot exceed its line amount. Fix the highlighted item.')
      // Upward-only price: a non-bundle item below its catalogue floor is illegal
      // (server enforces the same in post_sale_atomic_core — mig 322).
      if (cart.some(itemBelowFloor))
        throw new Error('A unit price is below its catalogue price. Raise it, or apply a discount to reduce the total.')
      // Defense-in-depth re-check against a stale/hacked client.
      if (headerDiscount > 0 && !can('sales.apply_discount')) throw new Error('Not authorized to apply discount')
      if (headerDiscountError) throw new Error(headerDiscountError)
      const pmts = effectivePayments
      if (pmts.length === 0) throw new Error('Select a payment method')

      const hasCredit = pmts.some(p => p.method === 'credit')

      if (hasCredit && !customerId) throw new Error('Select a customer for credit sales')
      if (!hasCredit && balanceDue > 0.01) throw new Error(`Balance due: ${format(balanceDue)}`)

      const advanceUsed = parseFloat(pmts.find(p => p.method === 'advance')?.amount ?? '0') || 0
      if (advanceUsed > 0 && !customerId) throw new Error('Customer required to apply advance')

      const cashLines = pmts.filter(p => p.method !== 'credit' && p.method !== 'advance')
      const cashLinesSum = cashLines.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
      const amountPaid = cashLinesSum
      const creditAmount = Math.max(0, grandTotal - cashLinesSum - advanceUsed)
      const paymentStatus: 'paid' | 'partial' | 'unpaid' =
        (cashLinesSum + advanceUsed) >= grandTotal - 0.01
          ? 'paid'
          : (cashLinesSum + advanceUsed) > 0.01
            ? 'partial'
            : 'unpaid'
      const creditDueDate = hasCredit ? getCreditDueDate(creditWeeks, creditCustomDate) : null
      const discountAmount = cart.reduce(
        (s, i) => s + (i.discountType === 'fixed' ? i.discountValue : i.price * i.qty * (i.discountValue / 100)),
        0,
      )

      const paymentLines = cashLines
        .map(p => ({
          method: p.method,
          account_id: p.coaAccountId || p.accountId,
          amount: parseFloat(p.amount) || 0,
          note: null as string | null,
        }))
        .filter(p => p.amount > 0 && !!p.account_id)

      const cashLinesAmountSum = paymentLines.reduce((s, p) => s + p.amount, 0)
      if (Math.abs(cashLinesAmountSum - amountPaid) > 0.01) {
        throw new Error('Payment account missing for one of the payment lines')
      }

      const summaryPaymentMethod: SalesOrderPaymentMethod = (() => {
        const n = paymentLines.length
        const unpaidCredit = creditAmount > 0.01
        if (n === 0) return unpaidCredit ? 'credit' : 'split'
        if (n >= 2 || unpaidCredit) return 'split'
        return normalisePaymentMethod(paymentLines[0].method)
      })()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: rpcResult, error: rpcErr } = await (supabase as any).rpc('post_sale_atomic', {
        p_tenant_id: tid,
        p_location_id: session.location_id,
        p_sale: {
          order_number: '',
          customer_id: customerId ?? null,
          status: 'final',
          sale_type: 'pos',
          source: 'pos',
          subtotal,
          discount_amount: discountAmount,
          sale_discount_amount: headerDiscount,
          sale_discount_percent: saleDiscountType === 'percent' ? saleDiscountValue : null,
          sale_discount_reason: saleDiscountReason || null,
          tax_amount: totalVat,
          total: grandTotal,
          amount_paid: amountPaid,
          payment_method: summaryPaymentMethod,
          payment_status: paymentStatus,
          payment_due: creditAmount,
          payment_due_date: creditDueDate,
          created_at: new Date().toISOString(),
        },
        p_items: cartItemsWithTotals.flatMap(item => {
          // Bundle items expand into one row per component, each carrying
          // bundle_id/bundle_name (snapshot labels) and its own VAT. The header-
          // discount allocation for this bundle is sub-split across components
          // with the SAME shared helper — so component shares sum EXACTLY to the
          // bundle's line allocation.
          if (item.bundleId && item.components) {
            const subs = item.components.map(c => item.qty * c.unit_qty * c.unit_price)
            const totalSub = subs.reduce((s, n) => s + n, 0)
            const lineDisc = itemDiscount(item)
            const headerShares = allocateProportional(subs, headerAllocation.get(item.id) ?? 0)
            return item.components.map((c, j) => {
              const subtotal = subs[j]
              const discShare = totalSub > 0 ? lineDisc * (subtotal / totalSub) : 0
              const lineNet = subtotal - discShare - headerShares[j]
              const compTax = c.is_vatable ? lineNet * vatRate / 100 : 0
              return {
                product_id: c.product_id,
                quantity: item.qty * c.unit_qty,
                unit_price: c.unit_price,
                discount_amount: discShare,
                discount_type: 'fixed' as 'fixed' | 'percent',
                discount_percent: 0,
                tax_percent: c.is_vatable ? vatRate : 0,
                tax_amount: compTax,
                price_inc_tax: lineNet + compTax,
                total: lineNet,
                bundle_id: (item.bundleId ?? null) as string | null,
                bundle_name: item.bundleName ?? null,
              }
            })
          }
          const net = item.total - (headerAllocation.get(item.id) ?? 0)
          const lineVat = item.isVatable ? net * vatRate / 100 : 0
          return [{
            product_id: item.productId,
            quantity: item.qty,
            unit_price: item.price,
            discount_amount: item.discountType === 'fixed'
              ? item.discountValue
              : item.price * item.qty * (item.discountValue / 100),
            discount_type: item.discountType,
            discount_percent: item.discountType === 'percent' ? item.discountValue : 0,
            tax_percent: item.isVatable ? vatRate : 0,
            tax_amount: lineVat,
            price_inc_tax: net + lineVat,
            total: net,
            bundle_id: null,
            bundle_name: null,
          }]
        }),
        p_payment_lines: paymentLines,
        p_created_by: user?.id ?? null,
        p_pos_session_id: session.id,
        p_apply_advance_amount: advanceUsed,
        p_idempotency_key: idem.get(),
      })
      if (rpcErr) throw new Error(rpcErr.message ?? 'Failed to post sale')

      return {
        orderId: rpcResult.sale_id as string,
        orderNumber: rpcResult.order_number as string,
        saleSubtotal: subtotal,
        saleDiscount: discountAmount,
        saleHeaderDiscount: headerDiscount,
        saleHeaderDiscountReason: saleDiscountReason || null,
        saleTax: totalVat,
        saleTotal: grandTotal,
        changeAmt: change,
        cartSnapshot: [...cartItemsWithTotals],
        // Receipt customer-account block (render-only): who owes, and how much of
        // this sale settled now (cash + applied advance). AR delta = total − this.
        saleCustomerId: customerId ?? null,
        saleAmountPaid: cashLinesSum + advanceUsed,
      }
    },
    onSuccess: ({ orderNumber, saleSubtotal, saleDiscount, saleHeaderDiscount, saleHeaderDiscountReason, saleTax, saleTotal, changeAmt, cartSnapshot, saleCustomerId, saleAmountPaid }) => {
      idem.reset()
      setShowSuccess({ orderNumber, total: saleTotal, change: changeAmt })
      setReceiptData({
        orderNumber,
        items: cartSnapshot,
        subtotal: saleSubtotal,
        discount: saleDiscount,
        headerDiscount: saleHeaderDiscount,
        headerDiscountReason: saleHeaderDiscountReason,
        tax: saleTax,
        total: saleTotal,
        change: changeAmt,
        cashierName,
        registerName: session?.warehouses?.name ?? 'POS Session',
        customerName,
        customerId: saleCustomerId,
        customerPhone: null,
        customerAddress: null,
        amountPaid: saleAmountPaid,
        nowOwing: null,
        paymentMethod: payTab === 'multiple' ? 'Split' : (payTab ?? 'cash'),
        timestamp: new Date(),
      })
      // Render-only: fetch the customer's authoritative AR balance AFTER the sale
      // (the same getCustomerBalance the Customers card uses) for the "now owing"
      // block, and merge it into the receipt. Suppressed for walk-in/no-customer.
      if (saleCustomerId && readReceiptExtras(tenant?.settings).showBalance) {
        getCustomerBalance(supabase, tid, saleCustomerId)
          .then(bal => setReceiptData(prev => (prev ? { ...prev, nowOwing: bal } : prev)))
          .catch(() => { /* balance is a courtesy line; never block the receipt */ })
      }
      // Render-only: pull the customer's phone + address so the receipt always prints
      // them for a customer sale (the POS picker only carried name/id). Bounded read by id.
      if (saleCustomerId) {
        db('customers')
          .select('name, business_name, phone, address')
          .eq('tenant_id', tid)
          .eq('id', saleCustomerId)
          .single()
          .then((res: { data: { phone: string | null; address: string | null } | null }) => {
            const c = res.data
            if (c) setReceiptData(prev => (prev ? { ...prev, customerPhone: c.phone ?? null, customerAddress: c.address ?? null } : prev))
          })
          .catch(() => { /* phone/address are courtesy lines; never block the receipt */ })
      }
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['pos-products-all'] })
      qc.invalidateQueries({ queryKey: ['customer-advance-pos'] })
      setTimeout(() => { setShowSuccess(null); setReceiptData(null); clearCart() }, 5000)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // ── Fullscreen ──────────────────────────────────────────────────────────
  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {})
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
    }
  }

  // ── Render gates ─────────────────────────────────────────────────────────
  if (!tenant?.id || !user?.id) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50">
        <div className="h-10 w-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin" />
        <p className="ml-3 text-gray-500 text-sm">Loading session…</p>
      </div>
    )
  }

  if (sessionLoading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50">
        <div className="h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!session) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50 p-6">
        <div className="card max-w-sm w-full text-center space-y-4">
          <div className="h-16 w-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto">
            <ShoppingBag className="h-8 w-8 text-orange-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Session not found</h2>
          <p className="text-sm text-gray-500">This POS session no longer exists or has been closed.</p>
          <Link to="/pos" className="btn-primary block">Back to POS</Link>
        </div>
      </div>
    )
  }

  const timeStr = now.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const dateStr = now.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })

  // ── Can complete ────────────────────────────────────────────────────────
  const splitLinesHaveAccounts = splitLines.every(l => parseFloat(l.amount || '0') <= 0 || !!l.accountId)
  const cartHasNegative = cart.some(itemIsNegative)
  // Reject (never clamp) any line priced below its catalogue floor: block
  // checkout until the price is raised to catalogue or the line is removed.
  // Clamping would silently "fix" the price and let a cashier without discount
  // permission push a lower price through; rejecting forces sell-at-catalogue or
  // the permission-gated discount. Server floor (mig 322) stays the backstop.
  const cartHasBelowFloor = cart.some(itemBelowFloor)
  const canComplete = cart.length > 0 && !cartHasNegative && !cartHasBelowFloor && !!payTab && (
    payTab === 'credit' ? !!customerId :
    payTab === 'cash' ? !!selectedCashId && balanceDue <= 0.01 :
    payTab === 'bank' ? !!selectedBankId && balanceDue <= 0.01 :
    payTab === 'pos' ? !!selectedPosId && balanceDue <= 0.01 :
    payTab === 'multiple' ? splitLinesHaveAccounts && splitRemaining <= 0.01 :
    false
  )

  return (
    <>
    <div className="fixed inset-0 bg-gray-100 flex flex-col z-40 overflow-hidden print:hidden">

      {/* ═══ HEADER ROW 1 — orange bar ═══════════════════════════════════════ */}
      <div className="bg-orange-600 flex items-center px-4 py-1.5 gap-4 flex-shrink-0">
        {/* Left */}
        <Link to="/dashboard" className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Dashboard</span>
        </Link>

        {/* Session location — fixed, non-clickable */}
        <div className="flex items-center gap-1.5 text-white/90 text-sm flex-shrink-0">
          <MapPin className="h-3.5 w-3.5" />
          <span className="font-semibold">{session.warehouses?.name ?? 'POS'}</span>
        </div>

        <div className="flex items-center gap-2 text-white/75 text-xs min-w-0 flex-1">
          <CalendarDays className="h-3.5 w-3.5 hidden sm:inline-flex flex-shrink-0" />
          <span className="hidden sm:inline">{dateStr}</span>
          <Clock className="h-3.5 w-3.5 ml-1 hidden sm:inline-flex flex-shrink-0" />
          <span className="tabular-nums hidden sm:inline">{timeStr}</span>
          <User className="h-3.5 w-3.5 sm:ml-1 flex-shrink-0" />
          <span className="truncate">Cashier: {cashierName}</span>
          <span className="ml-2 flex-shrink-0">·</span>
          <span className="inline-flex flex-shrink-0"><SessionTimer openedAt={session.opened_at} /></span>
        </div>

        {/* Online-orders alert — visible on every breakpoint (the notification a
            cashier must not miss); tap to jump to the Online Orders list. */}
        {canSeeOnlineOrders && (
          <Link
            to="/sell/online-orders"
            title="Online Orders"
            aria-label={`Online orders needing action: ${unattendedOnline}`}
            className="ml-auto md:ml-0 relative flex-shrink-0 p-1.5 rounded-lg text-white hover:bg-orange-700 transition-colors"
          >
            <Bell className="h-4 w-4" />
            {unattendedOnline > 0 && (
              <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {unattendedOnline}
              </span>
            )}
          </Link>
        )}

        {/* Right — icon actions (desktop) */}
        <div className={cn('items-center gap-1 hidden md:flex', canSeeOnlineOrders ? 'ml-1' : 'ml-auto')}>
          {[
            { icon: <Calculator className="h-4 w-4" />, tip: 'Calculator', action: () => setShowCalc(c => !c) },
            {
              icon: (
                <div className="relative">
                  <Pause className="h-4 w-4" />
                  {holds.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-white text-orange-600 text-[9px] font-bold flex items-center justify-center">
                      {holds.length}
                    </span>
                  )}
                </div>
              ),
              tip: 'Suspended Sales',
              action: () => setShowHolds(true),
            },
            { icon: isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />, tip: 'Fullscreen', action: toggleFullscreen },
            {
              icon: <Lock className="h-4 w-4" />,
              tip: 'Close POS',
              action: () => setShowCloseModal(true),
            },
          ].map((btn, i) => (
            <button
              key={i}
              onClick={btn.action}
              title={btn.tip}
              className="p-1.5 rounded-lg text-white hover:bg-orange-700 transition-colors"
            >
              {btn.icon}
            </button>
          ))}
        </div>

        {/* Right — overflow menu (mobile) */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          aria-label="More actions"
          className="ml-auto md:hidden flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-white hover:bg-orange-700 transition-colors relative"
        >
          <MoreVertical className="h-5 w-5" />
          {holds.length > 0 && (
            <span className="absolute top-1 right-1 h-3.5 w-3.5 rounded-full bg-white text-orange-600 text-[9px] font-bold flex items-center justify-center">
              {holds.length}
            </span>
          )}
        </button>
      </div>

      {/* ═══ Mobile overflow menu ═══════════════════════════════════════════ */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="absolute top-12 right-2 bg-white text-gray-900 rounded-xl shadow-lg overflow-hidden min-w-[220px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setShowCalc(c => !c); setMobileMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 min-h-[44px] text-sm"
            >
              <Calculator className="h-5 w-5 text-gray-500" />
              <span>Calculator</span>
            </button>
            <button
              onClick={() => { setShowHolds(true); setMobileMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 min-h-[44px] text-sm"
            >
              <Pause className="h-5 w-5 text-gray-500" />
              <span className="flex-1 text-left">Suspended Sales</span>
              {holds.length > 0 && (
                <span className="h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {holds.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { toggleFullscreen(); setMobileMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 min-h-[44px] text-sm"
            >
              {isFullscreen ? <Minimize2 className="h-5 w-5 text-gray-500" /> : <Maximize2 className="h-5 w-5 text-gray-500" />}
              <span>Fullscreen</span>
            </button>
            <div className="border-t border-gray-200" />
            <button
              onClick={() => { setShowCloseModal(true); setMobileMenuOpen(false) }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 text-red-600 min-h-[44px] text-sm"
            >
              <Lock className="h-5 w-5" />
              <span>Close POS</span>
            </button>
          </div>
        </div>
      )}

      {/* ═══ HEADER ROW 2 — search + categories ═════════════════════════════ */}
      <div className="bg-white border-b flex-shrink-0">
        <div className="px-4 pt-2 pb-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products by name, SKU or scan barcode…"
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
              autoFocus
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ═══ MAIN AREA ═══════════════════════════════════════════════════════ */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">

        {/* ═══ PRODUCT GRID — wide main area (left on lg, top on mobile) ═══════ */}
        {/* Products-first DOM: no order-* utilities — DOM order alone gives
            products-left on lg and products-on-top on mobile. */}
        <div className="w-full lg:flex-1 overflow-y-auto p-3 bg-gray-50 min-w-0 min-h-[55vh] lg:min-h-0">
          {/* Hidden input for the inline tile image-add (admin/manager only) */}
          {canEditProduct && (
            <input
              ref={posImgFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => handlePosImagePick(e.target.files?.[0])}
            />
          )}
          {/* Category pills — up to 4 inline + "Other categories" overflow menu.
              Filtering is unchanged: every pill / menu item just calls setActiveCat. */}
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              <button
                onClick={() => setActiveCat('all')}
                className={cn(
                  'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                  activeCat === 'all'
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                )}
              >
                All
              </button>
              {categories.slice(0, 4).map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCat(cat.id)}
                  className={cn(
                    'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                    activeCat === cat.id
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                  )}
                >
                  {cat.name}
                </button>
              ))}
              {categories.length > 4 && (
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setShowCatMenu(v => !v)}
                    onBlur={() => setTimeout(() => setShowCatMenu(false), 150)}
                    className={cn(
                      'flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                      overflowActiveCat
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                    )}
                  >
                    {overflowActiveCat ? overflowActiveCat.name : 'Other categories'}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  {showCatMenu && (
                    <div className="absolute top-full mt-1 left-0 z-40 min-w-[12rem] max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg py-1">
                      {categories.slice(4).map(cat => (
                        <button
                          key={cat.id}
                          onMouseDown={() => { setActiveCat(cat.id); setShowCatMenu(false) }}
                          className={cn(
                            'w-full text-left px-3 py-2 text-sm hover:bg-orange-50',
                            activeCat === cat.id ? 'text-orange-600 font-semibold' : 'text-gray-700'
                          )}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {loadingProducts ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 animate-pulse h-44" />
              ))}
            </div>
          ) : !hasAnyProducts ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 text-center">
              <Package className="h-10 w-10 mb-3 text-gray-300" />
              <p className="text-base font-medium text-gray-600">No products found</p>
              <p className="text-xs text-gray-400 mt-1">Add products in Inventory to sell here</p>
              <p className="text-xs text-gray-300 mt-1">Tenant: {tid ? `${tid.slice(0, 8)}…` : 'not set'}</p>
              <button
                onClick={() => refetchProducts()}
                className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600"
              >
                Retry Loading Products
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400 text-center">
              <AlertTriangle className="h-8 w-8 mb-2" />
              <p className="text-sm">No products match your filter</p>
              {search && <button onClick={() => setSearch('')} className="text-xs text-orange-500 mt-1">Clear search</button>}
              {activeCat !== 'all' && <button onClick={() => setActiveCat('all')} className="text-xs text-orange-500 mt-1">Clear category filter</button>}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
              {filteredBundles.map(b => {
                const derivedPrice = bundleDerivedPrice(b)
                const availableQty = bundleAvailableQty(b, locationStockMap)
                const inCartQty = cart.find(i => i.bundleId === b.id)?.qty ?? 0
                const isAtMax = flashingMaxId === `bundle:${b.id}`
                return (
                  <BundleCard
                    key={`bundle:${b.id}`}
                    bundle={b}
                    derivedPrice={derivedPrice}
                    availableQty={availableQty}
                    inCartQty={inCartQty}
                    locationName={sessionLocationName}
                    onClick={() => {
                      if (inCartQty >= availableQty) {
                        setFlashingMaxId(`bundle:${b.id}`)
                        setTimeout(() => setFlashingMaxId(null), 1000)
                        return
                      }
                      addBundleToCart(b)
                    }}
                    isAtMax={isAtMax}
                  />
                )
              })}
              {products.map(p => {
                // Plain (non-variant) line qty drives the inline stepper; total
                // across productId (incl. variant lines) drives the corner badge.
                const plainLine = cart.find(i => i.id === p.id)
                const cartQty = plainLine?.qty ?? 0
                const inCartTotal = cart.filter(i => i.productId === p.id).reduce((s, i) => s + i.qty, 0)
                return (
                  <ProductCard
                    key={p.id}
                    product={p}
                    cartQty={cartQty}
                    inCartTotal={inCartTotal}
                    onClick={() => handleProductClick(p)}
                    onInc={() => addToCart(p)}
                    onDec={() => updateQty(p.id, cartQty - 1)}
                    isFlashing={flashingId === p.id}
                    isAtMax={flashingMaxId === p.id}
                    locationStock={locationStockFor(p.id)}
                    locationName={sessionLocationName}
                    canEditProduct={canEditProduct}
                    onAddImage={openPosImagePicker}
                    isUploadingImage={uploadingImgId === p.id}
                  />
                )
              })}
            </div>
          )}
        </div>

        {/* ═══ ORDER PANEL — cart + tenders (right on lg, below on mobile) ═══════ */}
        <div className="flex flex-col bg-white border-l w-full lg:w-[460px] lg:flex-shrink-0 min-w-0 min-h-[60vh] lg:min-h-0">

          {/* ── "Sell to" customer selector ── */}
          {/* Restyle only: same custSearch / customerId / custResults wiring.
              Default DISPLAY is "Select"; walk-in and "no selection" both keep
              customerId === null, so guest checkout is unchanged. */}
          <div className="px-3 py-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Sell to</span>
              <button
                onClick={() => setShowQuickAdd(true)}
                className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
              >
                <Plus className="h-3.5 w-3.5" /> Add Customer
              </button>
            </div>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                placeholder="Select"
                value={customerId ? (customerName ?? '') : custSearch}
                onChange={e => {
                  if (customerId) {
                    setCustomerId(null)
                    setCustomerName(null)
                  }
                  setCustSearch(e.target.value)
                  setShowCustomerDropdown(true)
                }}
                onFocus={() => { if (!customerId) setShowCustomerDropdown(true) }}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
              />
              {customerId && (
                <button
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => {
                    setCustomerId(null)
                    setCustomerName(null)
                    setCustSearch('')
                    if (payTab === 'credit') setPayTab(null)
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="h-3.5 w-3.5 text-gray-400 hover:text-red-400" />
                </button>
              )}

              {/* Selector dropdown — Walk-in first, then search results */}
              {showCustomerDropdown && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-56 overflow-y-auto">
                  <button
                    className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50"
                    onMouseDown={() => {
                      setCustomerId(null)
                      setCustomerName(null)
                      setCustSearch('')
                      setShowCustomerDropdown(false)
                      if (payTab === 'credit') setPayTab(null)
                    }}
                  >
                    <p className="font-medium text-gray-700">Walk-in customer</p>
                  </button>
                  {custSearch.length < 2 && (
                    <p className="px-3 py-2 text-xs text-gray-400 border-t border-gray-50">Type a name, email or phone to search…</p>
                  )}
                  {custResults.length === 0 && custSearch.length >= 2 && (
                    <p className="px-3 py-2 text-xs text-gray-400 border-t border-gray-50">No customers found</p>
                  )}
                  {custResults.map(c => (
                    <button
                      key={c.id}
                      className="w-full text-left px-3 py-2 hover:bg-orange-50 border-t border-gray-50"
                      onMouseDown={() => {
                        setCustomerId(c.id)
                        setCustomerName(c.business_name || c.name)
                        setCustSearch('')
                        setShowCustomerDropdown(false)
                      }}
                    >
                      <p className="text-sm font-medium text-gray-900">{c.business_name || c.name}</p>
                      <p className="text-xs text-gray-400">
                        {c.phone}{c.outstanding_balance > 0 ? ` • ${format(c.outstanding_balance)} outstanding` : ''}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {availableDeposit > 0 && (
              <div className="mt-1.5 flex items-center gap-1.5 bg-green-50 rounded-xl px-3 py-1.5 text-xs text-green-700 font-medium">
                <Zap className="h-3.5 w-3.5" />
                Advance Balance: {format(availableDeposit)} available
              </div>
            )}
          </div>

          {/* Cart — dense spreadsheet-style table (Product · Qty · Disc. · Amt · ⋯).
              Pure presentation; all qty/discount/delete handlers are reused verbatim. */}
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-300 text-center py-8 px-3">
                <ShoppingBag className="h-10 w-10 mb-2 text-gray-200" />
                <p className="text-sm font-medium text-gray-400">Tap a product to start a sale</p>
                <p className="text-xs mt-1 text-gray-300">or scan a barcode</p>
              </div>
            ) : (
              <div>
                {/* Table header — sticky at the top of the scrolling list */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 grid grid-cols-[minmax(0,1fr)_3.25rem_4.5rem_5rem_1.25rem] items-center gap-x-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  <span>Product</span>
                  <span className="text-center">Qty</span>
                  <span className="text-center">Disc.</span>
                  <span className="text-right">Amt</span>
                  <span aria-hidden="true" />
                </div>
                {/* Rows — one compact line each */}
                {cartItemsWithTotals.map(item => {
                  const isBundle = !!item.bundleId && !!item.components
                  // Bundle max qty derives live from the per-component stock map
                  // (same formula as the picker). Plain products fall back to
                  // their per-location current_stock.
                  const maxQty = isBundle && item.components
                    ? bundleAvailableQty(
                        { id: item.bundleId!, name: item.bundleName ?? '', description: null, components: item.components },
                        locationStockMap,
                      )
                    : (sessionLocationId ? locationStockFor(item.productId) : Infinity)
                  const atMax = maxQty !== Infinity && item.qty >= maxQty
                  return (
                    <div key={item.id} className={cn(
                      "border-b border-gray-100 animate-cart-slide-in",
                      isBundle && "bg-purple-50/40",
                    )}>
                      <div className="grid grid-cols-[minmax(0,1fr)_3.25rem_4.5rem_5rem_1.25rem] items-center gap-x-1.5 px-3 py-1">
                        {/* Product — name only (SKU removed) */}
                        <div className="min-w-0 flex items-center">
                          {isBundle && <span className="mr-1 text-[8px] font-bold uppercase px-1 rounded bg-purple-100 text-purple-700 flex-shrink-0">Bundle</span>}
                          <span className="text-xs font-medium text-gray-900 leading-tight break-words min-w-0">{item.name}</span>
                          {!isBundle && item.isVatable && <span className="ml-1 text-[8px] font-semibold text-orange-500 flex-shrink-0">VAT</span>}
                        </div>
                        {/* Qty — typeable number + small up/down (typed path honors
                            the same maxQty stock cap as the ▲ stepper) */}
                        <QtyCell qty={item.qty} max={maxQty} onChange={n => updateQty(item.id, n)} label={item.name} />
                        {/* Disc. — compact input + ₦/% toggle (gated by pos.edit_discount) */}
                        {canApplyDiscount ? (
                          <div className="flex items-center">
                            <input
                              type="number"
                              min={0}
                              max={item.discountType === 'percent' ? 100 : undefined}
                              step="0.01"
                              value={item.discountValue || ''}
                              onChange={e => updateItemDiscount(item.id, item.discountType, parseFloat(e.target.value) || 0)}
                              className="w-full min-w-0 h-7 px-1 border border-gray-200 rounded-l text-[10px] text-right focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
                              title="Discount"
                              placeholder="0"
                            />
                            <button
                              onClick={() => updateItemDiscount(item.id, item.discountType === 'percent' ? 'fixed' : 'percent', item.discountValue)}
                              className={cn(
                                'h-7 w-5 flex items-center justify-center rounded-r border border-l-0 text-[9px] flex-shrink-0',
                                item.discountType === 'percent'
                                  ? 'bg-orange-50 border-orange-300 text-orange-600'
                                  : 'bg-white border-gray-200 text-gray-500'
                              )}
                              title={item.discountType === 'percent' ? 'Percent discount' : 'Fixed-amount discount'}
                            >
                              {item.discountType === 'percent' ? <Percent className="h-2.5 w-2.5" /> : <span>₦</span>}
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-500 text-right truncate" title="You don't have permission to apply discounts">
                            {itemDiscount(item) > 0 ? `-${format(itemDiscount(item))}` : '—'}
                          </span>
                        )}
                        {/* Amt — line total, right-aligned */}
                        <span className="text-xs font-bold text-gray-900 text-right tabular-nums truncate">{format(item.total)}</span>
                        {/* Trash */}
                        <button onClick={() => setCart(prev => prev.filter(i => i.id !== item.id))} title="Remove" className="flex items-center justify-center text-gray-300 hover:text-red-400">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* Editable unit price (upward-only). Bundle prices are the
                          sum of their components, so they stay read-only. */}
                      {!isBundle && (
                        <div className="px-3 pb-1 flex items-center gap-1.5">
                          <span className="text-[9px] text-gray-400 uppercase tracking-wide flex-shrink-0">Unit ₦</span>
                          <input
                            type="number"
                            min={item.cataloguePrice}
                            step="0.01"
                            value={item.price}
                            onChange={e => updateItemPrice(item.id, parseFloat(e.target.value) || 0)}
                            title="Unit price. Can be raised above catalogue; to reduce, apply a discount."
                            className={cn(
                              'h-6 w-24 px-1.5 border rounded text-[10px] text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white',
                              itemBelowFloor(item)
                                ? 'border-red-300 ring-1 ring-red-300'
                                : item.price > item.cataloguePrice + 0.005
                                  ? 'border-green-300'
                                  : 'border-gray-200',
                            )}
                          />
                          {itemBelowFloor(item) ? (
                            <span className="text-[9px] text-red-600 font-medium leading-tight flex-1 min-w-0">Price is below the catalogue price ({format(item.cataloguePrice)}). To sell lower, use the Discount column instead.</span>
                          ) : item.price > item.cataloguePrice + 0.005 ? (
                            <span className="text-[9px] text-green-600 font-medium">above catalogue {format(item.cataloguePrice)}</span>
                          ) : null}
                        </div>
                      )}
                      {atMax && (
                        <p className="px-3 pb-1 text-[9px] text-amber-600 font-medium">Only {maxQty} available</p>
                      )}
                      {itemIsNegative(item) && (
                        <p className="px-3 pb-1 text-[10px] text-red-600 font-medium">Discount cannot exceed line amount</p>
                      )}
                      {isBundle && item.components && (
                        <ul className="px-3 pb-1.5 ml-2 text-[10px] text-gray-500 space-y-0.5">
                          {item.components.map(c => (
                            <li key={c.product_id} className="flex justify-between">
                              <span className="truncate">└─ {c.unit_qty * item.qty}× {c.product_name}</span>
                              <span className="ml-2 text-gray-400">{format(c.unit_price)} ea</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* ── Expandable payment details (above action bar) ── */}
          {payTab === 'cash' && (
            <div className="border-t flex-shrink-0 bg-white px-3 pt-2 pb-1 space-y-1.5">
              {cashAccounts.length === 0 ? (
                <p className="text-xs text-red-500">No cash account found. Add one in Settings → Payment Accounts.</p>
              ) : cashAccounts.length === 1 ? null : (
                <>
                  <button
                    type="button"
                    onClick={() => setAccountModal('cash')}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold bg-white hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-300"
                  >
                    <span className={cn('truncate', selectedCashId ? 'text-gray-900' : 'text-gray-400')}>
                      {cashAccounts.find(a => a.id === selectedCashId)?.name ?? 'Select cash account'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  </button>
                  {!selectedCashId && (
                    <p className="text-[10px] text-amber-600 font-medium">Pick the cash account to record this payment against.</p>
                  )}
                </>
              )}
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₦</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={cashInput}
                  onChange={e => { setCashInput(e.target.value); setCashDirty(true) }}
                  placeholder="Amount received"
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300"
                  autoFocus
                />
              </div>
              {(parseFloat(cashInput) || 0) > grandTotal + 0.01 && (
                <div className="text-xs font-semibold text-green-600 bg-green-50 rounded-lg px-3 py-1">
                  Will create {format((parseFloat(cashInput) || 0) - grandTotal)} change — confirm amount is correct.
                </div>
              )}
              {(parseFloat(cashInput) || 0) < grandTotal - 0.01 && cashInput !== '' && (
                <div className="flex justify-between text-xs text-red-500 font-semibold">
                  <span>Still owed</span><span>{format(grandTotal - (parseFloat(cashInput) || 0))}</span>
                </div>
              )}
            </div>
          )}

          {payTab === 'bank' && (
            <div className="border-t flex-shrink-0 bg-white px-3 pt-2 pb-1 space-y-1.5">
              {bankAccounts.length === 0 ? (
                <p className="text-xs text-gray-500">No bank accounts set up. Go to Settings → Payment Accounts.</p>
              ) : (
                <>
                  {bankAccounts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setAccountModal('bank')}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold bg-white hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    >
                      <span className={cn('truncate', selectedBankId ? 'text-gray-900' : 'text-gray-400')}>
                        {bankAccounts.find(a => a.id === selectedBankId)?.name ?? 'Select bank account'}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </button>
                  )}
                  {!selectedBankId && (
                    <p className="text-[10px] text-amber-600 font-medium">Pick the receiving bank account.</p>
                  )}
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₦</span>
                    <input type="number" min={0} step="0.01" value={bankInput}
                      onChange={e => { setBankInput(e.target.value); setBankDirty(true) }} placeholder="Amount"
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" autoFocus />
                  </div>
                </>
              )}
            </div>
          )}

          {payTab === 'pos' && (
            <div className="border-t flex-shrink-0 bg-white px-3 pt-2 pb-1 space-y-1.5">
              {posAccounts.length === 0 ? (
                <p className="text-xs text-gray-500">No POS terminals set up. Go to Settings → Payment Accounts.</p>
              ) : (
                <>
                  {posAccounts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setAccountModal('pos')}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold bg-white hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    >
                      <span className={cn('truncate', selectedPosId ? 'text-gray-900' : 'text-gray-400')}>
                        {posAccounts.find(a => a.id === selectedPosId)?.name ?? 'Select POS terminal'}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </button>
                  )}
                  {!selectedPosId && (
                    <p className="text-[10px] text-amber-600 font-medium">Pick the POS terminal.</p>
                  )}
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₦</span>
                    <input type="number" min={0} step="0.01" value={posInput}
                      onChange={e => { setPosInput(e.target.value); setPosDirty(true) }} placeholder="Amount"
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-300" autoFocus />
                  </div>
                </>
              )}
            </div>
          )}

          {payTab === 'credit' && (
            <div className="border-t flex-shrink-0 bg-white px-3 pt-2 pb-1 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Full amount on credit</span>
                <span className="text-xs font-bold text-orange-600">{format(grandTotal)}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {[1, 2, 4, 6, 8, 12].map(w => (
                  <button key={w}
                    onClick={() => { setCreditWeeks(w); setCreditCustomDate('') }}
                    className={cn(
                      'text-xs px-2.5 py-1 rounded-lg border font-medium transition-colors',
                      creditWeeks === w && !creditCustomDate
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-gray-200 text-gray-600 hover:border-orange-300'
                    )}>{w}w</button>
                ))}
                <input type="date" value={creditCustomDate}
                  onChange={e => { setCreditCustomDate(e.target.value); setCreditWeeks(null) }}
                  className={cn(
                    'text-xs px-2 py-1 rounded-lg border focus:outline-none focus:ring-1 focus:ring-orange-400',
                    creditCustomDate ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  )} />
              </div>
              {(creditWeeks || creditCustomDate) && (
                <p className="text-xs text-gray-500">Due: {getCreditDueDate(creditWeeks, creditCustomDate)}</p>
              )}
            </div>
          )}

          {payTab === 'multiple' && (
            <div className="border-t flex-shrink-0 bg-orange-50 px-3 py-1.5 flex items-center justify-between">
              <span className="text-xs text-orange-700 font-medium">Split payment panel →</span>
              <button onClick={() => setShowSplitPanel(true)} className="text-xs text-orange-600 underline">Open</button>
            </div>
          )}

          {/* Payment summary inline */}
          {payTab && payTab !== 'multiple' && (change > 0.01 || balanceDue > 0.01) && (
            <div className="flex gap-3 px-3 py-1 bg-white border-t border-gray-100 flex-shrink-0">
              {change > 0.01 && payTab !== 'credit' && payTab !== 'cash' && (
                <span className="text-xs text-green-600 font-semibold">Will create {format(change)} change — confirm amount is correct.</span>
              )}
              {balanceDue > 0.01 && payTab !== 'credit' && (
                <span className="text-xs text-red-500 font-semibold">Due: {format(balanceDue)}</span>
              )}
            </div>
          )}

          {/* ── Whole-invoice discount (PR D/D3b) — dedicated row above the totals bar ── */}
          {cart.length > 0 && (canHeaderDiscount || headerDiscount > 0) && (
            <div className="border-t flex-shrink-0 px-3 py-1.5 bg-white space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500 flex-shrink-0">Discount</span>
                {canHeaderDiscount ? (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center flex-shrink-0">
                      <input type="number" min={0} max={saleDiscountType === 'percent' ? 100 : undefined} step="0.01"
                        value={saleDiscountValue || ''} onChange={e => setSaleDiscountValue(parseFloat(e.target.value) || 0)}
                        className="w-20 h-11 px-1.5 border border-gray-200 rounded-l-lg text-[11px] text-right focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
                        title="Whole-invoice discount" placeholder="0" />
                      <button type="button"
                        onClick={() => setSaleDiscountType(saleDiscountType === 'percent' ? 'fixed' : 'percent')}
                        className={cn('h-11 w-11 flex items-center justify-center rounded-r-lg border border-l-0 text-[10px] flex-shrink-0',
                          saleDiscountType === 'percent' ? 'bg-orange-50 border-orange-300 text-orange-600' : 'bg-white border-gray-200 text-gray-500')}
                        title={saleDiscountType === 'percent' ? 'Percent discount' : 'Fixed-amount discount'}>
                        {saleDiscountType === 'percent' ? <Percent className="h-3 w-3" /> : <span>₦</span>}
                      </button>
                    </div>
                    {headerDiscount > 0 && <span className="text-xs font-semibold text-red-500 tabular-nums w-20 text-right">-{format(headerDiscount)}</span>}
                  </div>
                ) : (
                  <span className="text-xs font-semibold text-red-500 tabular-nums">-{format(headerDiscount)}</span>
                )}
              </div>
              {headerDiscountError && <p className="text-[11px] text-red-600 text-right">{headerDiscountError}</p>}
              {canHeaderDiscount && headerDiscount > 0 && (
                <input type="text" value={saleDiscountReason} onChange={e => setSaleDiscountReason(e.target.value)}
                  placeholder="Reason (optional, internal)"
                  className="w-full h-11 px-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
              )}
            </div>
          )}

          {/* ── Totals breakdown: Subtotal / VAT / Total ── */}
          {cart.length > 0 && (
            <div className="border-t flex-shrink-0 px-3 py-1.5 bg-gray-50 flex items-center justify-between text-xs">
              <div className="flex flex-col leading-tight">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-semibold text-gray-800 tabular-nums">{format(subtotal)}</span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-gray-500">VAT @ {vatRate}%</span>
                <span className="font-semibold text-gray-800 tabular-nums">{format(totalVat)}</span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-gray-500">Items</span>
                <span className="font-bold text-gray-900 tabular-nums">{cart.reduce((s, i) => s + i.qty, 0)}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex flex-col leading-tight">
                  <span className="text-gray-500">Discount</span>
                  <span className="font-semibold text-red-500 tabular-nums">-{format(totalDiscount)}</span>
                </div>
              )}
              <div className="flex flex-col leading-tight items-end">
                <span className="text-gray-500">Total</span>
                <span className="text-base font-black text-orange-600 tabular-nums">{format(grandTotal)}</span>
              </div>
            </div>
          )}

          {/* Credit-limit soft warning (non-blocking) */}
          {!!customerId && customerCreditInfo && customerCreditInfo.credit_limit > 0 && creditPortion > 0 && (customerCreditInfo.outstanding + creditPortion) > customerCreditInfo.credit_limit && (
            <div className="border-t flex-shrink-0 bg-amber-50 px-3 py-2">
              <p className="text-xs font-semibold text-amber-800 leading-tight">
                ⚠ This puts {customerCreditInfo.display_name} over their credit limit.
              </p>
              <p className="text-[11px] text-amber-700 mt-0.5 leading-snug">
                {customerCreditInfo.outstanding > customerCreditInfo.credit_limit ? (
                  <>Current outstanding <span className="font-semibold">{format(customerCreditInfo.outstanding)}</span> already exceeds the <span className="font-semibold">{format(customerCreditInfo.credit_limit)}</span> limit · This sale adds <span className="font-semibold">{format(creditPortion)}</span> on credit · New balance <span className="font-semibold">{format(customerCreditInfo.outstanding + creditPortion)}</span> ({format((customerCreditInfo.outstanding + creditPortion) - customerCreditInfo.credit_limit)} over).</>
                ) : (
                  <>Credit limit: <span className="font-semibold">{format(customerCreditInfo.credit_limit)}</span> · Outstanding: <span className="font-semibold">{format(customerCreditInfo.outstanding)}</span> · This sale: <span className="font-semibold">+{format(creditPortion)}</span> on credit · New balance: <span className="font-semibold">{format(customerCreditInfo.outstanding + creditPortion)}</span> ({format((customerCreditInfo.outstanding + creditPortion) - customerCreditInfo.credit_limit)} over).</>
                )}
              </p>
            </div>
          )}

          {/* ── Single bottom bar: Total | payment tabs | Hold | Complete Sale ── */}
          <div className="border-t flex-shrink-0 px-3 py-2.5 bg-white flex flex-wrap items-center gap-2.5">
            {/* Payment method tabs */}
            {(
              [
                { id: 'cash', label: 'Cash', icon: <Banknote className="h-3 w-3" /> },
                { id: 'bank', label: 'Bank', icon: <Building2 className="h-3 w-3" /> },
                { id: 'pos', label: 'POS', icon: <CreditCard className="h-3 w-3" /> },
                { id: 'credit', label: 'Credit', icon: <Receipt className="h-3 w-3" />, requiresCustomer: true },
                { id: 'multiple', label: 'Split', icon: <GitMerge className="h-3 w-3" /> },
              ] as const
            ).map(btn => {
              const disabled = btn.id === 'credit' && !customerId
              return (
                <button
                  key={btn.id}
                  onClick={() => selectPayTab(btn.id as 'cash' | 'bank' | 'pos' | 'credit' | 'multiple')}
                  disabled={disabled}
                  title={disabled ? 'Select a customer for credit' : btn.label}
                  className={cn(
                    'flex-1 min-w-[64px] min-h-[48px] flex items-center justify-center gap-1 py-2.5 rounded-lg border text-xs font-semibold transition-all',
                    disabled
                      ? 'border-gray-100 text-gray-300 cursor-not-allowed bg-gray-50'
                      : payTab === btn.id
                        ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:border-orange-300 hover:bg-orange-50'
                  )}
                >
                  {btn.icon}
                  {btn.label}
                </button>
              )
            })}

            <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

            {/* Hold / Suspend */}
            <button
              className="flex items-center justify-center gap-1 px-4 py-2.5 min-h-[48px] rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200 transition-colors flex-shrink-0 disabled:opacity-40"
              onClick={() => { if (cart.length > 0) setShowSuspendPrompt(true) }}
              disabled={cart.length === 0}
              title="Suspend sale"
            >
              <Pause className="h-3.5 w-3.5" /> Hold
            </button>

            {/* Complete Sale */}
            <button
              className="flex items-center justify-center gap-1 px-5 py-2.5 min-h-[48px] rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 disabled:opacity-50 transition-colors flex-shrink-0"
              onClick={() => submitSale.mutate()}
              disabled={submitSale.isPending || !canComplete}
            >
              {submitSale.isPending
                ? <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <CheckCircle className="h-3.5 w-3.5" />}
              Complete Sale
            </button>
          </div>
        </div>

      </div>

      {/* ═══ CALCULATOR ══════════════════════════════════════════════════════ */}
      {showCalc && (
        <POSCalculator
          onUseAmount={(amount) => {
            if (payTab === 'bank') setBankInput(amount.toString())
            else if (payTab === 'pos') setPosInput(amount.toString())
            else setCashInput(amount.toString())
          }}
          onClose={() => setShowCalc(false)}
        />
      )}

      {/* ═══ SUCCESS OVERLAY ═════════════════════════════════════════════════ */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm w-full mx-4 shadow-2xl">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Sale Complete!</h2>
            <p className="text-gray-500 text-sm mb-4">{showSuccess.orderNumber}</p>
            <p className="text-3xl font-black text-orange-600 mb-2">{format(showSuccess.total)}</p>
            {showSuccess.change > 0.01 && (
              <p className="text-green-600 font-semibold mb-4">Change: {format(showSuccess.change)}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200"
              >
                Print Receipt
              </button>
              <button
                onClick={() => { setShowSuccess(null); setReceiptData(null); clearCart() }}
                className="flex-1 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600"
              >
                New Sale
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ VARIANT PICKER ══════════════════════════════════════════════════ */}
      {variantProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setVariantProduct(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">{variantProduct.name}</h3>
              <button onClick={() => setVariantProduct(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            {fetchingVariants ? (
              <div className="flex justify-center py-6">
                <div className="h-6 w-6 border-3 border-orange-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {variantOptions.map(v => (
                  <button
                    key={v.id}
                    disabled={v.quantity <= 0}
                    onClick={() => {
                      addToCart(variantProduct, v.id, v.name, v.selling_price)
                      setVariantProduct(null)
                    }}
                    className={cn(
                      'p-3 rounded-xl border-2 text-left transition-all',
                      v.quantity <= 0
                        ? 'border-gray-100 opacity-40 cursor-not-allowed'
                        : 'border-gray-200 hover:border-orange-400 hover:bg-orange-50 active:scale-95'
                    )}
                  >
                    <p className="text-sm font-semibold text-gray-900">{v.name}</p>
                    <p className="text-sm font-bold text-orange-600">{format(v.selling_price)}</p>
                    <p className={cn('text-xs mt-0.5', v.quantity <= 0 ? 'text-red-400' : 'text-gray-400')}>
                      {v.quantity <= 0 ? 'Out of stock' : `${v.quantity} in stock`}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ QUICK ADD CUSTOMER PANEL ════════════════════════════════════════ */}
      {showQuickAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end" onClick={() => setShowQuickAdd(false)}>
          <div className="w-80 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-gray-900">Add Customer</h3>
              <button onClick={() => setShowQuickAdd(false)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Business Name (optional)</label>
                <input
                  className="input"
                  value={qaBusinessName}
                  onChange={e => setQaBusinessName(e.target.value)}
                  placeholder="Business / Company Name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Contact Name *</label>
                <input
                  className="input"
                  value={qaName}
                  onChange={e => setQaName(e.target.value)}
                  placeholder="Full Name"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Phone *</label>
                <input
                  className="input"
                  type="tel"
                  value={qaPhone}
                  onChange={e => setQaPhone(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Email (optional)</label>
                <input
                  className="input"
                  type="email"
                  value={qaEmail}
                  onChange={e => setQaEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Credit Limit (optional)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₦</span>
                  <input
                    className="input pl-8"
                    type="number"
                    min={0}
                    value={qaCreditLimit}
                    onChange={e => setQaCreditLimit(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex-shrink-0">
              <button
                className="w-full btn-primary py-3"
                onClick={() => quickAddCustomer.mutate()}
                disabled={quickAddCustomer.isPending}
              >
                {quickAddCustomer.isPending ? 'Adding…' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PAYMENT ACCOUNT MODAL (Cash / Bank / POS) ═══════════════════════ */}
      {accountModal === 'cash' && (
        <PaymentAccountModal
          title="Select Cash Account"
          subtitle={`Total: ${format(grandTotal)}`}
          accounts={cashAccounts}
          selectedId={selectedCashId}
          onSelect={(id) => { setSelectedCashId(id); setAccountModal(null) }}
          onClose={() => setAccountModal(null)}
          emptyHint="No cash account found. Add one in Settings → Payment Accounts."
        />
      )}
      {accountModal === 'bank' && (
        <PaymentAccountModal
          title="Select Bank Account"
          subtitle={`Total: ${format(grandTotal)}`}
          accounts={bankAccounts}
          selectedId={selectedBankId}
          onSelect={(id) => { setSelectedBankId(id); setAccountModal(null) }}
          onClose={() => setAccountModal(null)}
          emptyHint="No bank accounts set up. Go to Settings → Payment Accounts."
        />
      )}
      {accountModal === 'pos' && (
        <PaymentAccountModal
          title="Select POS Terminal"
          subtitle={`Total: ${format(grandTotal)}`}
          accounts={posAccounts}
          selectedId={selectedPosId}
          onSelect={(id) => { setSelectedPosId(id); setAccountModal(null) }}
          onClose={() => setAccountModal(null)}
          emptyHint="No POS terminals set up. Go to Settings → Payment Accounts."
        />
      )}

      {/* ═══ SPLIT PAYMENT PANEL ═════════════════════════════════════════════ */}
      {showSplitPanel && (
        <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col border-l">
          <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="font-bold text-gray-900">Split Payment</h3>
              <p className="text-xs text-gray-400">Total: {format(grandTotal)}</p>
            </div>
            <button onClick={() => setShowSplitPanel(false)}><X className="h-5 w-5 text-gray-400" /></button>
          </div>

          {/* Balance summary */}
          <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total</span>
              <span className="font-bold text-gray-900">{format(grandTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Paid</span>
              <span className="font-bold text-green-600">{format(splitTotalPaid)}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-gray-200 pt-1">
              <span className="text-gray-500">Remaining</span>
              <span className={cn('font-bold', splitRemaining > 0.01 ? 'text-red-500' : splitRemaining < -0.01 ? 'text-amber-500' : 'text-green-600')}>
                {splitRemaining > 0.01
                  ? format(splitRemaining)
                  : splitRemaining < -0.01
                    ? `Overpaid ${format(Math.abs(splitRemaining))}`
                    : '✓ Balanced'}
              </span>
            </div>
          </div>

          {/* Advance payment */}
          {availableDeposit > 0 && (
            <div className="px-4 py-3 border-b flex-shrink-0">
              <p className="text-xs text-gray-500 mb-1.5">
                Customer Advance Available: <span className="font-semibold text-green-600">{format(availableDeposit)}</span>
              </p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₦</span>
                <input
                  type="number"
                  min={0}
                  max={Math.min(availableDeposit, grandTotal)}
                  step="0.01"
                  value={splitAdvance}
                  onChange={e => setSplitAdvance(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                  placeholder="Use advance amount"
                />
              </div>
            </div>
          )}

          {/* Payment lines */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {splitLines.map((line) => (
              <div key={line.tempId} className="flex items-center gap-2">
                <select
                  value={line.accountId ?? ''}
                  onChange={e => {
                    const val = e.target.value
                    if (!val) {
                      setSplitLines(prev => prev.map(l => l.tempId === line.tempId
                        ? { ...l, method: 'cash', accountId: null, accountName: '' }
                        : l))
                      return
                    }
                    const opt = allPaymentOptions.find(o => o.accountId === val)
                    if (opt && opt.accountId) {
                      setSplitLines(prev => prev.map(l => l.tempId === line.tempId
                        ? { ...l, method: opt.method, accountId: opt.accountId, accountName: opt.label }
                        : l))
                    }
                  }}
                  className={cn(
                    'flex-1 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white',
                    line.accountId ? 'border-gray-200' : 'border-amber-300 text-amber-700'
                  )}
                >
                  <option value="">— Select account —</option>
                  {allPaymentOptions.map(opt => (
                    <option key={opt.accountId ?? ''} value={opt.accountId ?? ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-semibold">₦</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={line.amount}
                    onChange={e => setSplitLines(prev => prev.map(l => l.tempId === line.tempId ? { ...l, amount: e.target.value } : l))}
                    className="w-full pl-5 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs font-bold text-right focus:outline-none focus:ring-1 focus:ring-orange-400"
                    placeholder="0"
                  />
                </div>
                <button
                  onClick={() => setSplitLines(prev => prev.filter(l => l.tempId !== line.tempId))}
                  className="text-gray-300 hover:text-red-400 flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            {splitLines.length < 5 && (
              <button
                onClick={() => setSplitLines(prev => [...prev, {
                  tempId: Math.random().toString(36).slice(2),
                  method: 'cash',
                  accountId: null,
                  accountName: '',
                  amount: '',
                }])}
                className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-orange-300 hover:text-orange-500 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Payment Method
              </button>
            )}
          </div>

          {/* Complete sale from split panel */}
          <div className="p-4 border-t flex-shrink-0">
            {splitRemaining > 0.01 && (
              <p className="text-xs text-red-500 text-center mb-2">
                {format(splitRemaining)} still remaining
              </p>
            )}
            {splitRemaining < -0.01 && (
              <p className="text-xs text-amber-500 text-center mb-2">
                Change: {format(Math.abs(splitRemaining))}
              </p>
            )}
            <button
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-600 disabled:opacity-50 transition-colors"
              onClick={() => submitSale.mutate()}
              disabled={submitSale.isPending || splitRemaining > 0.01 || !splitLinesHaveAccounts}
            >
              {submitSale.isPending
                ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <CheckCircle className="h-5 w-5" />}
              Complete Sale
            </button>
            {!splitLinesHaveAccounts && (
              <p className="text-[10px] text-amber-600 text-center mt-1.5">Each payment line needs an account.</p>
            )}
          </div>
        </div>
      )}

      {/* ═══ SUSPENDED SALES PANEL ═══════════════════════════════════════════ */}
      {showHolds && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end" onClick={() => setShowHolds(false)}>
          <div className="w-80 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Suspended Sales ({holds.length})</h3>
              <button onClick={() => setShowHolds(false)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {holds.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No held sales</p>
              ) : holds.map(h => (
                <div key={h.id} className="rounded-xl border border-gray-100 p-3 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{h.customer_name ?? 'Walk-in'}</p>
                      <p className="text-xs text-gray-400">{(h.cart_data ?? []).length} items · {format(h.total_amount)}</p>
                      {h.note && <p className="text-xs text-gray-500 italic">{h.note}</p>}
                    </div>
                    <span className="text-xs text-gray-400">{timeAgo(h.held_at)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 btn-secondary text-xs py-1.5"
                      onClick={() => resumeHold.mutate(h)}
                      disabled={resumeHold.isPending}
                    >
                      Continue
                    </button>
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 border border-red-200"
                      onClick={() => deleteHold.mutate(h.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SUSPEND PROMPT ══════════════════════════════════════════════════ */}
      {showSuspendPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowSuspendPrompt(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-xs w-full mx-4 shadow-2xl space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">Suspend Sale</h3>
            <input
              className="input"
              placeholder="Note (optional)"
              value={suspendNote}
              onChange={e => setSuspendNote(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2">
              <button className="flex-1 btn-secondary" onClick={() => setShowSuspendPrompt(false)}>Cancel</button>
              <button
                className="flex-1 btn-primary"
                onClick={() => suspendSale.mutate()}
                disabled={suspendSale.isPending}
              >
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* ═══ PRINT RECEIPT (print-only, hidden on screen) — the shared InvoiceReceipt so the
        POS receipt, the Sale-Detail invoice and the Printer-Settings preview are one layout. */}
    {receiptData && (
      <InvoiceReceipt
        preview={false}
        paperWidth={tenant?.settings?.receipt_paper_width === '58' ? '58' : '80'}
        business={{
          name: tenant?.name ?? 'Store',
          address: tenant?.settings?.business_address ?? null,
          mobile: tenant?.settings?.business_mobile ?? null,
        }}
        meta={{
          docTitle: 'RECEIPT',
          number: receiptData.orderNumber,
          dateTime: receiptData.timestamp,
          servedBy: receiptData.cashierName,
          register: receiptData.registerName,
        }}
        customer={receiptData.customerId
          ? { name: receiptData.customerName ?? '', phone: receiptData.customerPhone, address: receiptData.customerAddress }
          : null}
        items={receiptData.items.map(itm => {
          const isBundle = !!itm.bundleId && !!itm.components
          return {
            name: `${isBundle ? '[BUNDLE] ' : ''}${itm.name}`,
            qty: itm.qty,
            unitPrice: itm.price,
            lineTotal: itm.total,
            components: isBundle && itm.components
              ? itm.components.map(c => ({ name: c.product_name, qty: c.unit_qty * itm.qty }))
              : undefined,
          }
        })}
        totals={{
          subtotal: receiptData.subtotal,
          discount: receiptData.headerDiscount,
          discountLabel: 'Discount',
          vat: receiptData.tax,
          total: receiptData.total,
          amountPaid: receiptData.amountPaid,
          change: receiptData.change,
        }}
        paymentMethod={receiptData.paymentMethod}
        account={{ show: receiptExtras.showBalance && !!receiptData.customerId, nowOwing: receiptData.nowOwing }}
        extras={receiptExtras}
        format={format}
      />
    )}

    {/* ═══ CLOSE SESSION MODAL ═══════════════════════════════════════════ */}
    {showCloseModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
        <div className="card w-full max-w-sm space-y-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
              <Lock className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Close this session?</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                You won&apos;t be able to add more sales to it. The End-of-Day report will open next.
              </p>
            </div>
          </div>
          <div>
            <label className="label">Closing notes (optional)</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={closingNotes}
              onChange={e => setClosingNotes(e.target.value)}
              placeholder="Anything to note for this session…"
              disabled={closeSession.isPending}
            />
          </div>
          <div className="flex gap-3">
            <button
              className="btn-secondary flex-1"
              onClick={() => setShowCloseModal(false)}
              disabled={closeSession.isPending}
            >
              Cancel
            </button>
            <button
              className="btn-danger flex-1"
              onClick={() => closeSession.mutate()}
              disabled={closeSession.isPending}
            >
              {closeSession.isPending ? 'Closing…' : 'Close Session'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
